// v5 navigation tree — labels/icons/ids copied verbatim from
// Alpaca Login Page/alpaca_intelligence_v5.html (design-system 6.1).
import { PAGE_MANIFEST, NAV_GROUP_DEFS, CATEGORY_DEFS } from '../features/performance/manifest';
import { NC_CATEGORIES } from '../features/notifications/data';
import { resolveNavIcon } from './legacyNavIcons';   // global legacy-icon resolver (2026-09-18)

export type Badge = 'intel' | 'view' | 'new' | 'ai';

export interface RailItem { id: string; icon: string; title: string; color?: string; sep?: boolean; }

// One rail icon per top-level module, in the same order as the sidebar.
// Each id is the module group's FIRST form, so clicking the icon opens that
// form directly (and the matching sidebar group auto-expands). Icons mirror
// the sidebar group icons. Permission gating (canSee) hides any icon whose
// landing form the user can't access — identical to the sidebar group.
// Rail icons are colourful EMOJI (real coloured glyphs from the OS emoji font)
// — matches the original performance HTML design's rail.  AppShell renders any
// non-`ti-` icon string as an emoji (see NavIcon).
export const railItems: RailItem[] = [
  { id: 'home', icon: '🏠', title: 'Home' },
  { id: 'execdash', icon: '📊', title: 'Executive Dashboard' },
  { id: 'avainsight', icon: '✨', title: 'AVA Insight' },
  // Performance Analytics — rail opens the first dashboard; the group expands.
  { id: 'pa-exec-kpi', icon: '📈', title: 'Performance Analytics' },
  // Notification Center — rail opens the Overview; the group expands.
  { id: 'nc-overview', icon: '🔔', title: 'Notification Center' },
  { id: 'sep1', icon: '', title: '', sep: true },
  // Task Tracker group — Calendar / Job Progress / Test Progress (FB11)
  { id: 'taskcal',       icon: '📋', title: 'Task Tracker' },
  { id: 'bl-coc',        icon: '🗂️', title: 'Sample Backlog' },
  { id: 'schedtask',     icon: '📅', title: 'Sample Scheduling' },
  { id: 'sampreg',       icon: '📍', title: 'Sample Tracking' },
  { id: 'fielddata',     icon: '🧪', title: 'Sampling Management' },
  { id: 'subouttest',    icon: '🔄', title: 'Sample SubContract Tracking' },
  { id: 'preplqueue',    icon: '⚗️', title: 'Sample Preparation' },
  { id: 'analysisq',     icon: '🔬', title: 'Analysis' },
  { id: 'sdmsval',       icon: '✅', title: 'Data Review' },
  { id: 'custreport',    icon: '📄', title: 'Reporting' },
  { id: 'trendanalysis', icon: '📉', title: 'QA/QC' },
  { id: 'acc-paymentterms', icon: '⚙️', title: 'Settings' },
  { id: 'maint-versionlog', icon: '🛠️', title: 'Maintenance' },
  { id: 'dc-collector',  icon: '🗄️', title: 'Data Center' },
];

/** `g`-prefix navigation chords (CLAUDE.md standing rule 14, §18.6, ADR-0013).
 *  Press `g` then `key` to jump to that module.  `targetId` is the nav id to
 *  open — AppShell routes through `railTarget(targetId)`, so it lands on that id
 *  when the user can see it, else the first visible leaf of its group
 *  (permission-safe, exactly like clicking the rail icon).  Most targets are the
 *  module's rail anchor; Settings targets its FIRST form (Analysis Department
 *  Chain) instead of the rail anchor, because the anchor (Payment Terms) also
 *  lives under Settings ▸ Utility and would land the user deep in Utility rather
 *  than at the top of the module (user report 2026-09-19).  Data-driven so the
 *  set is unit-tested (every targetId resolves to a real nav destination; keys
 *  must be unique). */
export interface NavChord { key: string; label: string; targetId: string; }
// The 17 documented `g`-chords (Keyboard Shortcuts Manual v3 §1) — one letter per
// screen, each `targetId` a real rail anchor or grouped leaf that `railTarget` opens
// permission-safe (list view first). Data-driven so navChords.test verifies every
// target resolves + keys are unique. Inventory has no dedicated operational module,
// so `g i` lands on the ICM Pending-Orders view (the closest inventory screen).
export const NAV_CHORDS: NavChord[] = [
  { key: 'h', label: 'Home',                   targetId: 'home' },
  { key: 'a', label: 'Analysis Queue',         targetId: 'analysisq' },
  { key: 'v', label: 'SDMS Result Validation', targetId: 'sdmsval' },
  { key: 'o', label: 'Result Approval',        targetId: 'sdmsappr' },
  { key: 'p', label: 'Sample Prep Batches',    targetId: 'prepbatches' },
  { key: 's', label: 'Sample Registration',    targetId: 'sampreg' },
  { key: 'r', label: 'Custom Reporting',       targetId: 'custreport' },
  { key: 'd', label: 'Data Center',            targetId: 'dc-collector' },
  { key: 'q', label: 'QC Batches',             targetId: 'qcbatches' },
  { key: 'f', label: 'Field Data Entry',       targetId: 'fielddata-entry' },
  { key: 'n', label: 'Notification Center',    targetId: 'nc-overview' },
  { key: 'i', label: 'Inventory (ICM)',        targetId: 'icm01' },
  { key: 'b', label: 'Sample Backlog',         targetId: 'bl-coc' },
  { key: 'm', label: 'Maintenance',            targetId: 'maint-versionlog' },
  { key: 't', label: 'Test Code',              targetId: 'set-testcode' },
  { key: 'u', label: 'Users',                  targetId: 'sec-users' },
  { key: 'x', label: 'Settings',               targetId: 'adc-chain' },
];

export interface NavItem { id: string; label: string; icon: string; iconColor?: string; badge?: Badge; count?: number; /** Optional sort-key override (pins an item next to a sibling, e.g. Technical Writer beside Test Code) — see settingsSortOrder.test.ts. */ sortAs?: string; }
/** A sub-group nested inside a top-level group — collapsible folder
 *  (e.g. Settings → Reporting → Regulatory Compliance → …). Sub-groups can
 *  themselves contain sub-groups, giving unlimited folder depth. */
export interface NavSubGroup {
  id: string;
  label: string;
  icon: string;
  iconColor?: string;
  badge?: Badge;
  /** Mixed children — clickable items and/or further nested sub-groups. */
  children: NavGroupChild[];
}
/** A child of a NavGroup can be: a clickable item, an uppercase sub-section
 *  label, or a fully-collapsible sub-group folder. */
export type NavGroupChild =
  | { kind: 'item'; item: NavItem }
  | { kind: 'subhead'; label: string }
  | { kind: 'subgroup'; subgroup: NavSubGroup };
export interface NavGroup { id: string; label: string; icon: string; iconColor?: string; badge?: Badge; items: NavItem[]; children?: NavGroupChild[]; }
export type NavEntry =
  | { kind: 'section'; label: string }
  | { kind: 'item'; item: NavItem }
  | { kind: 'sep' }
  | { kind: 'group'; group: NavGroup };

// ─── Performance Analytics group (built from the v8 manifest) ──────────────
// Categories → nav groups → dashboards live INSIDE the app's own left sidebar
// (expand "Performance Analytics" to see them — no second sidebar in content).
// Each dashboard nav id is `pa-<dashId>`; AppShell routes any `pa-*` page to
// PerformanceAnalyticsPage, which renders just that dashboard.  A dashboard that
// is cross-listed under several groups appears under each (same id, same page).
const PA_CAT_EMOJI: Record<string, string> = {
  'cat-exec': '📊', 'cat-labops': '🔬', 'cat-qa': '🛡️', 'cat-resources': '👥', 'cat-salesfin': '💰', 'cat-ai': '✨',
};
const PA_DASH_EMOJI: Record<string, string> = {
  'exec-kpi': '📊', 'sales-forecast': '🔮', 'lab-profit': '📈', 'sales-revclient': '💵', 'lab-revclient': '🧾',
  'sales-retention': '🤝', 'sales-atrisk': '🚨', 'sales-voltrend': '📉',
  'lab-myjobs': '🧪', 'lab-throughput': '🔁', 'lab-volume': '📊', 'lab-mix': '🥧', 'lab-backlog': '📦', 'lab-queue': '🔢',
  'lab-tatdash': '⏱️', 'lab-duedatetracking': '📆', 'lab-tatalerts': '🔔', 'lab-tat': '⏰', 'lab-recurrence': '♻️',
  'lab-qcpass': '✅', 'lab-rerun': '🔄', 'lab-oos': '⚠️', 'lab-recovery': '🎯', 'lab-holdtime': '⏳',
  'lab-part11': '🖊️', 'lab-coc': '🔗', 'lab-rejection': '🚫', 'lab-capa': '🛠️', 'lab-rollback': '↩️', 'lab-reportcycle': '📄',
  'lab-analyst': '👤', 'lab-shift': '🕒', 'lab-util': '🖥️',
  'sales-repscorecard': '🏅', 'sales-clientcontribution': '🏢', 'sales-winrate': '🏆', 'sales-pipeline': '🔻',
  'sales-newclients': '➕', 'sales-quotetime': '🕗', 'sales-lostreasons': '👎',
  'revenue-explorer': '🧭', 'revenue-trend': '📈', 'revenue-concentration': '🔺',
  'sales-revtest': '💹', 'sales-revrep': '🧑‍💼', 'sales-recurring': '🔁', 'sales-yoy': '📈',
  'lab-costsample': '💲', 'lab-billing': '📑', 'sales-aov': '🛒', 'sales-tenure': '📅', 'sales-quota': '🎯',
  'sales-seasonal': '🗓️', 'sales-whitespace': '🧩', 'sales-renewals': '📜', 'sales-pricerealization': '🏷️',
};

function buildPerfAnalyticsGroup(): NavGroup {
  const items: NavItem[] = [{ id: 'pa-catalog', label: 'Dashboard Catalog', icon: '🔎' }];
  const seen = new Set<string>(['pa-catalog']);
  const catalogChild: NavGroupChild = { kind: 'item', item: { id: 'pa-catalog', label: 'Dashboard Catalog', icon: '🔎' } };

  // ⭐ My Dashboards — the first sub-module, listing the default-pinned
  // dashboards (manifest `favoriteDefault`).  Emoji per form, existing captions.
  const myDashChildren: NavGroupChild[] = PAGE_MANIFEST.filter((p) => p.favoriteDefault).map((p) => {
    const id = `pa-${p.id}`;
    const icon = PA_DASH_EMOJI[p.id] ?? '📊';
    if (!seen.has(id)) { seen.add(id); items.push({ id, label: p.label, icon, badge: p.badge }); }
    return { kind: 'item', item: { id, label: p.label, icon, badge: p.badge } };
  });
  const myDashboardsChild: NavGroupChild = { kind: 'subgroup', subgroup: { id: 'pa-mydash', label: 'My Dashboards', icon: '⭐', children: myDashChildren } };

  const categoryChildren: NavGroupChild[] = CATEGORY_DEFS.map((cat) => {
    // Dashboards whose ANY nav-group belongs to this category (cross-listed),
    // de-duplicated — the forms sit DIRECTLY under the category sub-module, with
    // no intermediate sub-section headers (emoji icon per form).
    const groupKeys = Object.keys(NAV_GROUP_DEFS).filter((gk) => NAV_GROUP_DEFS[gk]!.parent === cat.id);
    const seenInCat = new Set<string>();
    const dashChildren: NavGroupChild[] = [];
    for (const gk of groupKeys) {
      for (const p of PAGE_MANIFEST.filter((pp) => pp.navGroups.includes(gk))) {
        if (seenInCat.has(p.id)) continue;
        seenInCat.add(p.id);
        const id = `pa-${p.id}`;
        const icon = PA_DASH_EMOJI[p.id] ?? '📊';
        if (!seen.has(id)) { seen.add(id); items.push({ id, label: p.label, icon, badge: p.badge }); }
        dashChildren.push({ kind: 'item', item: { id, label: p.label, icon, badge: p.badge } });
      }
    }
    return { kind: 'subgroup' as const, subgroup: { id: `pa-${cat.id}`, label: cat.label, icon: PA_CAT_EMOJI[cat.id] ?? '📊', children: dashChildren } };
  }).filter((c) => c.subgroup.children.length > 0);
  // ⭐ My Dashboards first, then the category sub-modules, the Catalog last (8 total).
  return { id: 'grp-perfanalytics', label: 'Performance Analytics', icon: 'ti-chart-histogram', items, children: [myDashboardsChild, ...categoryChildren, catalogChild] };
}
const PERF_ANALYTICS_GROUP = buildPerfAnalyticsGroup();

// ─── Notification Center group (AI-only design module, demo data) ──────────
// Structured like Performance Analytics: a top-level group whose sub-items all
// render inside one host page (NotificationCenterPage), keyed by `nc-<subId>`.
// Visibility: all `nc-*` ids are ALWAYS_VISIBLE (see navPermissions.ts) — no
// ALPACA NavigationView / backend yet, identical to the `pa-*` dashboards.
const NC_CONFIG_ITEMS: NavItem[] = [
  { id: 'nc-overview',   label: 'Overview',           icon: 'ti-layout-dashboard' },
  { id: 'nc-rules',      label: 'Notification Rules', icon: 'ti-adjustments-horizontal' },
  { id: 'nc-categories', label: 'Rule Categories',    icon: 'ti-category-2' },
  { id: 'nc-recipients', label: 'Recipient Groups',   icon: 'ti-users-group' },
  { id: 'nc-templates',  label: 'Email Templates',    icon: 'ti-mail' },
  { id: 'nc-delivery',   label: 'Delivery Log',       icon: 'ti-mail-fast' },
  // Lab-wide In-App (bell) pop-up: screen position + auto-close vs acknowledge (2026-10-01).
  { id: 'nc-popup-settings', label: 'Pop-up Settings', icon: 'ti-bell-cog' },
];
// Browse-by-Category sub-module — one leaf per rule category (`nc-cat-<code>`),
// built from the SAME NC_CATEGORIES demo data the page uses so the labels / icons
// / order can't drift.  Clicking a category opens the Notification Rules list
// filtered to it (NotificationCenterPage handles the `cat-<code>` subId).
const NC_BROWSE_ITEMS: NavItem[] = NC_CATEGORIES.map((c) => ({ id: `nc-cat-${c.code}`, label: c.name, icon: c.icon }));
const NOTIFICATION_CENTER_GROUP: NavGroup = {
  id: 'grp-notifications', label: 'Notification Center', icon: 'ti-bell', iconColor: 'var(--brand-accent, var(--ai-orange))',
  // flat list — search index + pageTitles / navMeta / pageGroupId lookups
  items: [...NC_CONFIG_ITEMS, ...NC_BROWSE_ITEMS],
  // rendered tree — the config forms only. The "Browse by Category" sub-module is
  // HIDDEN (owner request 2026-10-14): its leaves were built from the built-in demo
  // categories, not the lab's Rule Categories table. The `nc-cat-<code>` pages
  // stay reachable from the Overview's category rows, so their ids remain in
  // `items` above for titles / lookups.
  children: [
    ...NC_CONFIG_ITEMS.map((it) => ({ kind: 'item' as const, item: it })),
  ],
};

// ─── SYSTEM group — internal tools only ─────────────────────────────────────
// The whole Dashboard module (the DashboardData list + Assign / Designer /
// Preview) moved to the top-level Toolbox group on 2026-09-28 (user request);
// System now holds just the server-side tools.
const SYSTEM_TOOL_ITEMS: NavItem[] = [
  { id: 'servermon',         label: 'Server Monitor',    icon: 'ti-server' },
  { id: 'servermon-history', label: 'Server History',    icon: 'ti-chart-line' },
  { id: 'emailalerts',       label: 'Email Alerts',      icon: 'ti-mail-cog' },
  // Message Library + SQL Query Console + the Dashboard folder moved to the top-level Toolbox module (2026-09-28).
];
const SYSTEM_GROUP: NavGroup = {
  id: 'grp-system', label: 'System', icon: 'ti-server-2',
  // flat list — search index + pageTitles / navMeta / pageGroupId lookups
  items: [...SYSTEM_TOOL_ITEMS],
  children: SYSTEM_TOOL_ITEMS.map((it) => ({ kind: 'item' as const, item: it })),
};

// ─── TOOLBOX group (user request 2026-09-28) ────────────────────────────────
// A top-level "Toolbox" module, rendered right AFTER System, gathering the
// builder / dashboard / message / query tools in alphabetical order. Custom Report
// Builder (from Settings ▸ Reporting) and the ENTIRE Dashboard module + Message
// Library + SQL Query Console (from System) were MOVED here and REMOVED from their
// former homes, so Toolbox is now their sole location. The Dashboard leaves live in
// a "Dashboard" sub-folder: the DashboardData list + Assign Dashboard To User +
// Dashboard Designer + Preview. Leaf ids / routes / permissions are unchanged —
// only the sidebar placement moved.
const TOOLBOX_DASHBOARD_ITEMS: NavItem[] = [
  { id: 'dashboards',           label: 'Dashboard',                icon: 'ti-chart-pie' },
  { id: 'set-assign-dashboard', label: 'Assign Dashboard To User', icon: 'ti-layout-dashboard' },
  { id: 'dashdesigner',         label: 'Dashboard Designer',       icon: 'ti-layout-dashboard' },
  { id: 'dashpreview',          label: 'Dashboard Preview',        icon: 'ti-chart-donut' },
];
// flat list for search index / navMeta / palette — alphabetical top-level order
const TOOLBOX_ITEMS: NavItem[] = [
  { id: 'custreportbuilder', label: 'Custom Report Builder', icon: 'ti-layout-grid' },
  ...TOOLBOX_DASHBOARD_ITEMS,
  // Database ERD (2026-09-29): the ALPACA LIMS schema diagram (BTSoft ERD, 2026-06-22).
  { id: 'database-erd',      label: 'Database ERD',          icon: 'ti-sitemap' },
  { id: 'messagelibrary',   label: 'Message Library',       icon: 'ti-message-2' },
  // SDMS Template Builder (2026-09-29): port of the Alpaca Studio builder — designs the
  // SpreadSheetBuilder_* templates SDMS Result Entry runs.
  { id: 'sdms-template-builder', label: 'SDMS Template Builder', icon: 'ti-table-options' },
  { id: 'sqlconsole',        label: 'SQL Query Console',     icon: 'ti-database-search' },
];
const TOOLBOX_GROUP: NavGroup = {
  id: 'grp-toolbox', label: 'Toolbox', icon: '🧰',
  items: TOOLBOX_ITEMS,
  // rendered tree (alphabetical): Custom Report Builder · Dashboard folder · Database ERD · Message Library · SDMS Template Builder · SQL Query Console
  children: [
    { kind: 'item', item: { id: 'custreportbuilder', label: 'Custom Report Builder', icon: 'ti-layout-grid' } },
    { kind: 'subgroup', subgroup: { id: 'sg-toolbox-dashboard', label: 'Dashboard', icon: 'ti-layout-dashboard',
        children: TOOLBOX_DASHBOARD_ITEMS.map((it) => ({ kind: 'item' as const, item: it })) } },
    { kind: 'item', item: { id: 'database-erd', label: 'Database ERD', icon: 'ti-sitemap' } },
    { kind: 'item', item: { id: 'messagelibrary', label: 'Message Library', icon: 'ti-message-2' } },
    { kind: 'item', item: { id: 'sdms-template-builder', label: 'SDMS Template Builder', icon: 'ti-table-options' } },
    { kind: 'item', item: { id: 'sqlconsole', label: 'SQL Query Console', icon: 'ti-database-search' } },
  ],
};

export const navTree: NavEntry[] = [
  // Section heading dropped — was "Intelligence", removed per user feedback
  // (no AI/Intel branding in module names).
  { kind: 'section', label: 'Overview' },
  { kind: 'item', item: { id: 'home', label: 'Home', icon: '🏠' } },
  { kind: 'item', item: { id: 'execdash', label: 'Executive Dashboard', icon: '📊' } },
  { kind: 'item', item: { id: 'avainsight', label: 'AVA Insight', icon: '✨' } },
  // Performance Analytics — categories → groups → dashboards (v8 manifest) live
  // in the app's own left sidebar; content shows just the selected dashboard.
  { kind: 'group', group: PERF_ANALYTICS_GROUP },
  { kind: 'sep' },
  // Notification Center now lives UNDER Settings (moved 2026-06-26, alphabetised
  // between Method and Reporting) — see the grp-settings children below.
  // ─── To Do List (GoLive parity, user request 2026-09-18): the legacy XAF nav
  //     nests BOTH Task Tracker and Sample Backlog under one "To Do List" group.
  //     G6 now mirrors that exact hierarchy — Task Tracker first, Sample Backlog
  //     below it, both collapsible sub-groups, same child order/ids (routing +
  //     permissions unchanged; leaf ids are identical to before). This reverses
  //     the earlier FB11-v2 flattening on purpose, to match GoLive. ───
  { kind: 'group', group: { id: 'grp-todolist', label: 'To Do List', icon: 'ti-home', items: [], children: [
    { kind: 'subgroup', subgroup: { id: 'grp-task-tracker', label: 'Task Tracker', icon: 'ti-clipboard-list', children: [
      { kind: 'item', item: { id: 'taskcal',  label: 'Task Calendar',          icon: 'ti-calendar-stats', iconColor: 'var(--ai-green)' } },
      { kind: 'item', item: { id: 'jobprog',  label: 'Job Progress Tracking',  icon: 'ti-progress',       iconColor: 'var(--ai-blue)' } },
      { kind: 'item', item: { id: 'testprog', label: 'Test Progress Tracking', icon: 'ti-progress-check', iconColor: 'var(--ai-green)' } },
    ] } },
    // Every Sample Backlog entry is a DASHBOARD (dbo.DashboardData) — they share
    // the single legacy dashboard glyph (img:nav/bo-dashboard.png, shared-repo
    // icon decision 2026-09-18) rather than a per-item G6 icon.
    { kind: 'subgroup', subgroup: { id: 'grp-backlog', label: 'Sample Backlog', icon: 'ti-stack', children: [
      { kind: 'item', item: { id: 'bl-coc', label: '00 - Missing COC', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'bl-due', label: 'BL 00 - Due Samples', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'bl-track', label: 'BL 01 - Sample Tracking', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'bl-transfer', label: 'BL 02 - Pending Transfer', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'bl-subout', label: 'BL 03 - Subout Tracking', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'bl-prep', label: 'BL 04 - Sample Preparation', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'bl-sdms', label: 'BL 05 - SDMS Result Entry', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'bl-review', label: 'BL 06 - Field Data Validation', icon: 'img:nav/bo-dashboard.png' } },
      // Numbers + order match the actual dbo.DashboardData titles (07 = Result
      // Approval, 08 = Reporting — the Review → Approve → Report workflow order).
      { kind: 'item', item: { id: 'bl-rappr', label: 'BL 07 - Result Approval', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'bl-report', label: 'BL 08 - Reporting', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'icm01', label: 'ICM01 - Pending Orders', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'icm02', label: 'ICM02 - Pending Validation', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'icm03', label: 'ICM03 - Pending Receipt', icon: 'img:nav/bo-dashboard.png' } },
      { kind: 'item', item: { id: 'icm05', label: 'ICM05 - Pending COA', icon: 'img:nav/bo-dashboard.png' } },
      // Pending Jobs — every in-flight job (AI_Backlog_PendingJobs_SP).  A
      // dbo.DashboardData dashboard like the entries above since 2026-09-28
      // (AI_Dashboard_PendingJobs_Seed.sql); gated by its role assignment.
      { kind: 'item', item: { id: 'bl-pjobs', label: 'Pending Jobs', icon: 'img:nav/bo-dashboard.png' } },
    ] } },
  ] } },
  { kind: 'sep' },
  // ─── Client Request (design v2.0) — what the client ASKED for, upstream of
  //     the sample lifecycle. Request Registration is the landing list; Service
  //     Allocation is the dispatch board over every SAVED service across all
  //     requests. Scheduling / Tracking / Route Tracking join in later waves,
  //     which is why this is its own group rather than an item elsewhere.
  { kind: 'group', group: { id: 'grp-clientrequest', label: 'Client Request', icon: 'ti-clipboard-text', items: [
    { id: 'reqreg',   label: 'Request Registration', icon: 'ti-clipboard-list' },
    { id: 'svcaloc',  label: 'Service Allocation',   icon: 'ti-adjustments-horizontal' },
    { id: 'svcsched', label: 'Service Scheduling',   icon: 'ti-calendar-month' },
    { id: 'svctrack', label: 'Service Tracking',     icon: 'ti-activity' },
    { id: 'rttrack',  label: 'Route Tracking',       icon: 'ti-route' },
    // The SAME shared audit viewer Data Center routes to (id at-clientrequest,
    // mapped in AuditTrailPage) — a second doorway, not a second screen.
    { id: 'at-clientrequest', label: 'Audit Trail',    icon: 'ti-history' },
  ] } },
  { kind: 'sep' },
  // ─── Sample lifecycle: 4 top-level groups (no "Samples" parent wrapper) ───
  { kind: 'group', group: { id: 'grp-sample-scheduling', label: 'Sample Scheduling', icon: 'ti-calendar', items: [
    { id: 'schedtask', label: 'Scheduled Task Tracking', icon: 'ti-calendar-stats' },
    { id: 'sampaloc',  label: 'Sampling Allocation',     icon: 'ti-grid-dots' },
    // Read-only design-concept screens ported from the approved prototypes
    // (Sampling-History-v24 / Sample-Sites-Scheduling-Frequency-ListView-v14),
    // built on the shared FormTitle / AppToolbar / AppDataGrid (static sample data).
    { id: 'samplinghistory', label: 'Sampling History',           icon: 'ti-calendar-check' },
    { id: 'siteschedfreq',   label: 'Site Scheduling Frequency',  icon: 'ti-calendar-time' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-sample-tracking', label: 'Sample Tracking', icon: 'ti-map-pin', items: [
    // flat list — search index + pageTitles / navMeta / pageGroupId lookups
    { id: 'sampreg',      label: 'Sample Registration',          icon: 'ti-clipboard-list' },
    { id: 'sampreceipt',  label: 'Sample Receipt Notification',  icon: 'ti-bell' },
    { id: 'samplecancel', label: 'Sample Cancellation',          icon: 'ti-ban' },
    { id: 'sampphcheck',  label: 'Sample Container pH Check',    icon: 'ti-droplet' },
    { id: 'sample-in',        label: 'Sample In',        icon: 'ti-login-2' },
    { id: 'sample-out',       label: 'Sample Out',       icon: 'ti-logout-2' },
    { id: 'sample-disposal',  label: 'Sample Disposal',  icon: 'ti-trash' },
    { id: 'sample-inout-log', label: 'Sample In/Out Log', icon: 'ti-notebook' },
    { id: 'sample-location',  label: 'Sample Location',  icon: 'ti-map-pin' },
  ], children: [
    // rendered tree — the three flat forms, then the Barcode Sample Custody sub-module.
    // (When a group has `children`, the renderer shows ONLY `children`, so the flat
    //  forms MUST be repeated here as kind:'item' or they vanish from the sidebar.)
    { kind: 'item', item: { id: 'sampreg',     label: 'Sample Registration',         icon: 'ti-clipboard-list' } },
    { kind: 'item', item: { id: 'sampreceipt', label: 'Sample Receipt Notification', icon: 'ti-bell' } },
    // Sample Cancellation (2026-10-06) — legacy module SampleManagement/SampleCancellation.
    // XAF's "SampleManagement" nav group is THIS group (it is where Sample Registration lives).
    { kind: 'item', item: { id: 'samplecancel', label: 'Sample Cancellation', icon: 'ti-ban' } },
    { kind: 'subgroup', subgroup: { id: 'sg-barcode-custody', label: 'Barcode Sample Custody', icon: 'ti-barcode', children: [
      { kind: 'item', item: { id: 'sample-in',        label: 'Sample In',        icon: 'ti-login-2' } },
      { kind: 'item', item: { id: 'sample-out',       label: 'Sample Out',       icon: 'ti-logout-2' } },
      { kind: 'item', item: { id: 'sample-disposal',  label: 'Sample Disposal',  icon: 'ti-trash' } },
      { kind: 'item', item: { id: 'sample-inout-log', label: 'Sample In/Out Log', icon: 'ti-notebook' } },
      { kind: 'item', item: { id: 'sample-location',  label: 'Sample Location',  icon: 'ti-map-pin' } },
    ] } },
    // "Sample Container pH Check" is a DIRECT child of Sample Tracking, rendered
    // AFTER the Barcode Sample Custody group (a sibling of that folder, NOT inside
    // it) — matching legacy ALPACA's nav tree (user report 2026-09-25).
    { kind: 'item', item: { id: 'sampphcheck', label: 'Sample Container pH Check', icon: 'ti-droplet' } },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-sampling-mgmt', label: 'Sampling Management', icon: '🧪', items: [
    { id: 'fielddata-entry',      label: 'Field Data Entry',      icon: 'ti-clipboard-data' },
    { id: 'fielddata-validation', label: 'Field Data Validation', icon: '🗂', iconColor: '#185fa5' },
    { id: 'fielddata-approval',   label: 'Field Data Approval',   icon: '✅' },
    { id: 'fielddata',    label: 'Field Data Tracking', icon: '📈' },
    { id: 'fieldintake',  label: 'Field Intake & Job Creation', icon: 'ti-clipboard-check' },
    { id: 'samptransfer', label: 'Sample Transfer',     icon: 'ti-arrows-exchange' },
    // Bulk Route ID + Sampling Type per COC (AQT design note 2026-10-23).
    { id: 'routeconfig',  label: 'Route Configuration', icon: 'ti-route' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-subcontract', label: 'Sample SubContract Tracking', icon: 'ti-transfer', items: [
    // G5 captions + order (2026-10-23, owner: "build all these forms" — G5 reads
    // "Subcontract …"): Registration · Test Order · Result Entry · Result Validation ·
    // Notification Queue · PO Tracking.
    { id: 'suboutreg',      label: 'Subcontract Registration',       icon: 'ti-transfer-in' },
    { id: 'subouttest',     label: 'Subcontract Test Order',         icon: 'ti-file-invoice' },
    { id: 'suboutresentry', label: 'Subcontract Result Entry',       icon: 'ti-table' },
    { id: 'suboutresval',   label: 'Subcontract Result Validation',  icon: 'ti-checkup-list' },
    { id: 'suboutnotif',    label: 'Subcontract Notification Queue', icon: 'ti-send' },
    { id: 'suboutpo',       label: 'Subcontract PO Tracking',        icon: 'ti-receipt' },
  ] } },
  { kind: 'sep' },
  // ─── Analysis lifecycle: 3 top-level groups (no "Analysis" parent wrapper) ─
  { kind: 'group', group: { id: 'grp-sample-prep', label: 'Sample Preparation', icon: '⚗️', items: [
    { id: 'preplqueue',  label: 'Sample Prep Queue',   icon: 'ti-test-pipe' },
    { id: 'prepbatches', label: 'Sample Prep Batches', icon: 'ti-packages' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-analysis-core', label: 'Analysis', icon: 'ti-microscope', items: [
    // Daily QC data entry — OG nav item 'DailyQC' (view DailyQCLogBookBuilder_ListView_DailyQC).
    // Logbooks → history + trend → full-page entry (prototype ALPACA_Daily_QC_Data_Entry_v1).
    { id: 'dailyqc',    label: 'Daily QC',            icon: 'ti-calendar-check' },
    { id: 'analysisq',  label: 'Analysis Queue',      icon: 'ti-microscope' },
    { id: 'resexpress', label: 'Result Entry Express', icon: 'ti-edit' },
    { id: 'qcbatches',  label: 'QC Batches',          icon: 'ti-chart-dots' },
    // SDMS Result Entry — OG nav item id 'Spreadsheet' (view 'SDMS'), under the
    // DataEntry/Analysis group right after QC Batches. Opens the SDMS spreadsheet
    // (blank; Retrieve a batch). The QC Batch form's "Open SDMS" opens the same
    // screen seeded with a batch.
    { id: 'sdms',       label: 'SDMS',                icon: 'ti-table' },
    // PLM Result Entry ('plm') is HIDDEN from the menu, as in G5 (owner request
    // 2026-10-16): G5's NavigationItem "PLMV2" is Visible=False and the screen is
    // reached from the QC Batch form's "PLM" button. The route, its permission
    // (AI_Plm_ListView) and its title stay — see HIDDEN_NAV_ITEMS below.
    // PLM Daily QC (2026-10-16) — G5's own menu entry (Analysis › PLM Daily QC,
    // PlmV2DailyQc_ListView): every Daily QC read with Analyst / Result filters.
    { id: 'plm-dailyqc', label: 'PLM Daily QC',       icon: 'ti-target' },
    { id: 'restrack',   label: 'Result Tracking',     icon: 'ti-list-check' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-data-review', label: 'Data Review', icon: 'ti-circle-check', items: [
    { id: 'sdmsval',  label: 'SDMS Result Validation', icon: 'ti-settings' },
    // SDMS Result Approval — batch-level (mirrors ALPACA RawDataLevel3BatchReview);
    // shares the Validation nav icon (SDMS package intent). Job-level 'rappr'
    // stays unchanged. (Merged 2026-07-07.)
    { id: 'sdmsappr', label: 'SDMS Result Approval',   icon: 'ti-settings' },
    // Legacy job-level "Result Validation" (ALPACA Model.xafml Data Review group,
    // ViewId QCBatch_ListView_ResultValidation_Filter) — the manual counterpart of
    // 'rappr', paired the same way SDMS Result Validation pairs with SDMS Result
    // Approval. Backend (AnalysisWriter.ValidateJobOneAsync/ValidateResultsOneAsync,
    // AnalysisReader.ResultValidationListAsync) and the list/detail UI
    // (AnalysisListPage kind="result-validation-manual", ResultValidationDetail
    // mode="validation") were already fully built; this nav entry + the AppShell
    // route below were the only missing piece (user report 2026-07-17).
    { id: 'rval',     label: 'Result Validation',      icon: 'ti-circle-check' },
    { id: 'rappr',    label: 'Result Approval',        icon: 'ti-circle-check' },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-reporting', label: 'Reporting', icon: 'ti-report', items: [
    { id: 'custreport', label: 'Custom Reporting', icon: '📝' },
    // 'custreportbuilder' route is kept in AppShell for admin use but hidden
    // from the sidebar — user feedback 2026-06-06 ("not report build, this").
    { id: 'repval', label: 'Report Validation', icon: '📋' },
    // Report Approval — the 2nd-signature stage; shown only when approval is
    // required (gated in AppShell via reportApprovalRequired).
    { id: 'repappr', label: 'Report Approval', icon: 'ti-circle-check' },
    { id: 'repdel', label: 'Report Delivery', icon: '📦' },
    { id: 'reptrack', label: 'Report Tracking', icon: 'ti-files' },
    // Batch Reporting — query many jobs at once, one template per row, save all
    // to Report Validation (New Changes in Desgin prototype #screenBatch).
    { id: 'batchreport', label: 'Batch Reporting', icon: '📚' },
    { id: 'eddgen', label: 'EDD Report Generator', icon: 'ti-database-export' },
    { id: 'morlist',    label: 'MOR Reporting',       icon: 'ti-report-analytics' },
    { id: 'morpapp', label: 'MOR Pending Approval', icon: 'ti-clock-check' },
    { id: 'morpdel', label: 'MOR Pending Delivery', icon: 'ti-truck-delivery' },
    { id: 'msr', label: 'Monthly Summary Repository', icon: 'ti-archive' },
    // Ad Hoc Reporting — moved to the BOTTOM of the Reporting module per
    // user feedback 5/30/2026 #1.  Caption dropped "MOR" since the form
    // serves more than just monthly operations reporting.
    { id: 'morreport',  label: 'Ad Hoc Reporting', icon: 'ti-report-analytics' },
    // External Data Import — import external-lab results + limit monitoring.
    { id: 'edi-import', label: 'External Data Import', icon: 'ti-file-import' },
  ] } },
  { kind: 'sep' },
  // ── Accounting — legacy ALPACA top-level module (between Reporting and QA/QC).
  //    Shell only (2026-09-23): nav + Form Config catalog; forms route to the
  //    shared empty ModuleListView until each is ported. Legacy sub-groups:
  //    Invoicing · Quotes · Receivables (Model.xafml Item Id="Accounting").
  { kind: 'group', group: { id: 'grp-accounting', label: 'Accounting', icon: 'ti-cash', items: [
    { id: 'acct-invoice-queue',      label: 'Invoice Queue',      icon: 'ti-file-invoice' },
    { id: 'acct-invoice-validation', label: 'Invoice Validation', icon: 'ti-checkbox' },
    { id: 'acct-invoice-delivery',   label: 'Invoice Delivery',   icon: 'ti-truck-delivery' },
    { id: 'acct-invoice-tracking',   label: 'Invoice Tracking',   icon: 'ti-clock-check' },
    { id: 'acct-invoice-edd',        label: 'Invoice EDD Export', icon: 'ti-database-export' },
    { id: 'acct-quotes-open',        label: 'Open Quotes',        icon: 'ti-file-dollar' },
    { id: 'acct-quotes-tracking',    label: 'Quotes Tracking',    icon: 'ti-clock-check' },
    { id: 'acct-deposit',            label: 'Deposit',            icon: 'ti-cash-banknote' },
    { id: 'acct-collectionlog',      label: 'Collection Log',     icon: 'ti-notes' },
    { id: 'acct-deposit-edd',        label: 'Deposit EDD Export', icon: 'ti-database-export' },
    { id: 'acct-deposit-tracking',   label: 'Deposit Tracking',   icon: 'ti-clock-check' },
  ], children: [
    { kind: 'subgroup', subgroup: { id: 'sg-acct-invoicing', label: 'Invoicing', icon: 'ti-file-invoice', children: [
      { kind: 'item', item: { id: 'acct-invoice-queue',      label: 'Invoice Queue',      icon: 'ti-file-invoice' } },
      { kind: 'item', item: { id: 'acct-invoice-validation', label: 'Invoice Validation', icon: 'ti-checkbox' } },
      { kind: 'item', item: { id: 'acct-invoice-delivery',   label: 'Invoice Delivery',   icon: 'ti-truck-delivery' } },
      { kind: 'item', item: { id: 'acct-invoice-tracking',   label: 'Invoice Tracking',   icon: 'ti-clock-check' } },
      { kind: 'item', item: { id: 'acct-invoice-edd',        label: 'Invoice EDD Export', icon: 'ti-database-export' } },
    ] } },
    { kind: 'subgroup', subgroup: { id: 'sg-acct-quotes', label: 'Quotes', icon: 'ti-file-dollar', children: [
      { kind: 'item', item: { id: 'acct-quotes-open',     label: 'Open Quotes',     icon: 'ti-file-dollar' } },
      { kind: 'item', item: { id: 'acct-quotes-tracking', label: 'Quotes Tracking', icon: 'ti-clock-check' } },
    ] } },
    { kind: 'subgroup', subgroup: { id: 'sg-acct-receivables', label: 'Receivables', icon: 'ti-report-money', children: [
      { kind: 'item', item: { id: 'acct-deposit',          label: 'Deposit',            icon: 'ti-cash-banknote' } },
      { kind: 'item', item: { id: 'acct-collectionlog',    label: 'Collection Log',     icon: 'ti-notes' } },
      { kind: 'item', item: { id: 'acct-deposit-edd',      label: 'Deposit EDD Export', icon: 'ti-database-export' } },
      { kind: 'item', item: { id: 'acct-deposit-tracking', label: 'Deposit Tracking',   icon: 'ti-clock-check' } },
    ] } },
  ] } },
  { kind: 'sep' },
  { kind: 'group', group: { id: 'grp-qaqc', label: 'QA/QC', icon: 'ti-chart-dots', items: [
    // QA/QC ▸ Control Charts — Levey-Jennings / range / sterility charts over QC
    // results, with the Westgard rule set and the signed QC record (layout
    // preview with static sample data, 2026-08-16).
    { id: 'control-charts', label: 'Control Charts', icon: 'ti-chart-histogram' },
    { id: 'mdlstudies', label: 'MDL Studies', icon: 'ti-chart-dots-3', iconColor: 'var(--brand-accent, var(--ai-orange))' },
    // QA/QC ▸ Training Records — employee training + qualification (layout
    // preview with static sample data, 2026-08-14).
    { id: 'training-records', label: 'Training Records', icon: 'ti-certificate' },
    { id: 'trendanalysis', label: 'Trend Analysis', icon: 'ti-chart-line' },
    // QA/QC ▸ Internal Audit (G5 parity, 2026-10-27): Client Complaint + Nonconformity Corrective Action. The two
    // *-hist ids are the History lists the Correction Verification pages switch to (no menu entry of their own).
    { id: 'qa-cc-init',   label: 'Complaint Initiation',     icon: 'ti-settings' },
    { id: 'qa-cc-verif',  label: 'Correction Verification',  icon: 'ti-settings' },
    { id: 'qa-cc-hist',   label: 'Correction Verification History', icon: 'ti-settings' },
    { id: 'qa-nca-init',  label: 'Nonconformity Initiation', icon: 'ti-settings' },
    { id: 'qa-nca-verif', label: 'Correction Verification',  icon: 'ti-settings' },
    { id: 'qa-nca-hist',  label: 'Correction Verification History', icon: 'ti-settings' },
  ], children: [
    { kind: 'item', item: { id: 'control-charts', label: 'Control Charts', icon: 'ti-chart-histogram' } },
    { kind: 'subgroup', subgroup: { id: 'sg-qaqc-internalaudit', label: 'Internal Audit', icon: 'ti-clipboard-check', children: [
      { kind: 'subgroup', subgroup: { id: 'sg-qaqc-clientcomplaint', label: 'Client Complaint', icon: 'ti-message-report', children: [
        { kind: 'item', item: { id: 'qa-cc-init',  label: 'Complaint Initiation',    icon: 'ti-settings' } },
        { kind: 'item', item: { id: 'qa-cc-verif', label: 'Correction Verification', icon: 'ti-settings' } },
      ] } },
      { kind: 'subgroup', subgroup: { id: 'sg-qaqc-nca', label: 'Nonconformity Corrective Action', icon: 'ti-alert-triangle', children: [
        { kind: 'item', item: { id: 'qa-nca-init',  label: 'Nonconformity Initiation', icon: 'ti-settings' } },
        { kind: 'item', item: { id: 'qa-nca-verif', label: 'Correction Verification',  icon: 'ti-settings' } },
      ] } },
    ] } },
    { kind: 'item', item: { id: 'mdlstudies', label: 'MDL Studies', icon: 'ti-chart-dots-3', iconColor: 'var(--brand-accent, var(--ai-orange))' } },
    { kind: 'item', item: { id: 'training-records', label: 'Training Records', icon: 'ti-certificate' } },
    { kind: 'item', item: { id: 'trendanalysis', label: 'Trend Analysis', icon: 'ti-chart-line' } },
  ] } },
  // ── Inventory Management — two legacy sub-groups: Operations (workflow) + Alert
  //    (2026-09-22). items[] kept flat for global nav search; children[] drives the
  //    visual hierarchy matching the legacy XAF nav (Operations · Alert).
  { kind: 'group', group: { id: 'grp-icm-ops', label: 'Inventory Management', icon: 'ti-packages',
    items: [
      { id: 'icm-requisition',        label: 'Item Requisition',       icon: 'ti-list-check' },
      { id: 'icm-requisition-review', label: 'Requisition Review',     icon: 'ti-clipboard-check' },
      { id: 'icm-purchase-order',     label: 'Ordering Items',         icon: 'ti-shopping-cart' },
      { id: 'icm-receive-items',      label: 'Receiving Items',        icon: 'ti-package-import' },
      { id: 'icm-distribution',       label: 'Item Distribution',      icon: 'ti-arrow-right-circle' },
      { id: 'icm-consumption',        label: 'Item Consumption',       icon: 'ti-droplet' },
      { id: 'icm-fractional',         label: 'Fractional Consumption', icon: 'ti-droplet-half' },
      { id: 'icm-disposal',           label: 'Item Disposal',          icon: 'ti-trash' },
      { id: 'icm-vendor-coa',         label: 'Vendors COA',            icon: 'ti-certificate' },
      { id: 'icm-item-init',          label: 'Item Initialization',    icon: 'ti-database-import' },
      { id: 'icm-stock-watch',        label: 'Stock Watch',            icon: 'ti-eye' },
      { id: 'icm-stock-alert',        label: 'Stock Alert',            icon: 'ti-alert-triangle' },
      { id: 'icm-expiration-alert',   label: 'Expiration Alert',       icon: 'ti-calendar-exclamation' },
      { id: 'icm-req-tracking',          label: 'Item Requisition Tracking', icon: 'ti-clock-check' },
      { id: 'icm-process-tracking',      label: 'Item Process Tracking',     icon: 'ti-progress' },
      { id: 'icm-transaction-history',   label: 'Item Transaction History',  icon: 'ti-history' },
      { id: 'icm-cancelled-requisition', label: 'Cancelled Requisition',     icon: 'ti-ban' },
      { id: 'icm-lt-coa-tracking',       label: 'LT# COA Tracking',          icon: 'ti-certificate' },
    ],
    children: [
      // ── Operations — the full ICM workflow (legacy sub-group order)
      { kind: 'subgroup', subgroup: { id: 'sg-icm-operations', label: 'Operations', icon: 'ti-settings-2', children: [
        { kind: 'item', item: { id: 'icm-requisition',        label: 'Item Requisition',       icon: 'ti-list-check' } },
        { kind: 'item', item: { id: 'icm-requisition-review', label: 'Requisition Review',     icon: 'ti-clipboard-check' } },
        { kind: 'item', item: { id: 'icm-purchase-order',     label: 'Ordering Items',         icon: 'ti-shopping-cart' } },
        { kind: 'item', item: { id: 'icm-receive-items',      label: 'Receiving Items',        icon: 'ti-package-import' } },
        { kind: 'item', item: { id: 'icm-distribution',       label: 'Item Distribution',      icon: 'ti-arrow-right-circle' } },
        { kind: 'item', item: { id: 'icm-consumption',        label: 'Item Consumption',       icon: 'ti-droplet' } },
        { kind: 'item', item: { id: 'icm-fractional',         label: 'Fractional Consumption', icon: 'ti-droplet-half' } },
        { kind: 'item', item: { id: 'icm-disposal',           label: 'Item Disposal',          icon: 'ti-trash' } },
        { kind: 'item', item: { id: 'icm-vendor-coa',         label: 'Vendors COA',            icon: 'ti-certificate' } },
        { kind: 'item', item: { id: 'icm-item-init',          label: 'Item Initialization',    icon: 'ti-database-import' } },
      ] } },
      // ── Alert — read-only alert grids (legacy sub-group order: Stock Watch · Stock Alert · Expiration Alert)
      { kind: 'subgroup', subgroup: { id: 'sg-icm-alert', label: 'Alert', icon: 'ti-bell', children: [
        { kind: 'item', item: { id: 'icm-stock-watch',      label: 'Stock Watch',      icon: 'ti-eye' } },
        { kind: 'item', item: { id: 'icm-stock-alert',      label: 'Stock Alert',      icon: 'ti-alert-triangle' } },
        { kind: 'item', item: { id: 'icm-expiration-alert', label: 'Expiration Alert', icon: 'ti-calendar-exclamation' } },
      ] } },
      // ── Tracking — read-only history/audit grids (legacy sub-group order:
      // Item Requisition Tracking · Item Process Tracking · Item Transaction
      // History · Cancelled Requisition · LT# COA Tracking)
      { kind: 'subgroup', subgroup: { id: 'sg-icm-tracking', label: 'Tracking', icon: 'ti-history', children: [
        { kind: 'item', item: { id: 'icm-req-tracking',          label: 'Item Requisition Tracking', icon: 'ti-clock-check' } },
        { kind: 'item', item: { id: 'icm-process-tracking',      label: 'Item Process Tracking',     icon: 'ti-progress' } },
        { kind: 'item', item: { id: 'icm-transaction-history',   label: 'Item Transaction History',  icon: 'ti-history' } },
        { kind: 'item', item: { id: 'icm-cancelled-requisition', label: 'Cancelled Requisition',     icon: 'ti-ban' } },
        { kind: 'item', item: { id: 'icm-lt-coa-tracking',       label: 'LT# COA Tracking',          icon: 'ti-certificate' } },
      ] } },
    ],
  } },
  { kind: 'sep' },
  // ── Instrument Management — legacy ALPACA top-level module (Model.xafml Item
  //    Id="Assets" Caption="Instrument Management"). Shell only (2026-09-23).
  { kind: 'group', group: { id: 'grp-instrument', label: 'Instrument Management', icon: 'ti-device-desktop-analytics', items: [
    { id: 'instm-labware',     label: 'Instruments',       icon: 'ti-microscope' },
    { id: 'instm-maint-queue', label: 'Maintenance Queue', icon: 'ti-tool' },
    { id: 'instm-maint-log',   label: 'Maintenance Log',   icon: 'ti-clipboard-list' },
  ] } },
  { kind: 'sep' },
  // Document Management — top-level group (legacy ALPACA sidebar order: after
  // Instrument Management, before CRM). Qualifier (legacy dbo.Qualifiers) +
  // Documents, the published-document reader (built 2026-10-12).
  { kind: 'group', group: { id: 'grp-docmanagement', label: 'Document Management', icon: 'img:nav/action-navigation.png', items: [
    // Legacy ALPACA icons (DevExpress ImageResource): Document Management =
    // Action_Navigation, Qualifier = BO_Unknown (user request 2026-09-22).
    //
    // Documents is a SUBGROUP with one leaf per DocumentCategory — G5's shape,
    // restored on owner request 2026-10-12 ("in G5 2 Doc shows under Document
    // like separate forms"). ALPACA builds those children at runtime from the
    // live categories (WebApplication.cs :780 / NavigationRefreshController.cs
    // :2222) and so do we: `setDocumentCategoryNav` replaces them once
    // /published-documents/categories answers. `items` keeps the flat leaves for
    // leaf-collection / permissions; `children` is what the sidebar renders.
    //
    // ONE thing is deliberately NOT carried over: ALPACA also MERGEs a
    // dbo.NavigationItem row per category so each becomes its own grantable
    // permission target. Settled by data — those rows exist on the customer DB
    // but 19 roles hold 0 grants against them, so per-category permissions were
    // never used, and keying a grant on the category NAME breaks it on rename.
    // Here a category inherits the Documents grant (navPermissions resolves
    // `documents:<oid>` → `documents`). See the migration log §14.5 / Phase 1.
    { id: 'documents', label: 'Documents', icon: 'img:nav/bo-unknown.png' },
    // Qualifier was taken OFF the menu 2026-10-12 while its screens were being
    // reworked, and PUT BACK 2026-10-22 on owner request ("still missing") — legacy
    // order: Qualifier first, then the Documents subgroup. Page, route, API and
    // permissions were never touched.
    { id: 'qualifier', label: 'Qualifier', icon: 'img:nav/bo-unknown.png' },
  ], children: [
    { kind: 'item', item: { id: 'qualifier', label: 'Qualifier', icon: 'img:nav/bo-unknown.png' } },
    // Seeded with the plain Documents leaf so the form is reachable before the
    // categories arrive — and if the lab has none, which is also the state G5
    // would leave an empty group in.
    { kind: 'subgroup', subgroup: { id: 'sg-documents', label: 'Documents', icon: 'img:nav/bo-unknown.png',
      children: [{ kind: 'item', item: { id: 'documents', label: 'Documents', icon: 'img:nav/bo-unknown.png' } }] } },
  ] } },
  { kind: 'sep' },
  // ── CRM — legacy ALPACA top-level module (Model.xafml Item Id="CRM"). Shell
  //    only (2026-09-23). Sub-groups: Workplace · Accounts (Prospects/Quotes/
  //    Clients) · Contact Logs · Unfollowed Accounts.
  { kind: 'group', group: { id: 'grp-crm', label: 'CRM', icon: 'ti-users-group', items: [
    { id: 'crm-scheduler',            label: 'Schedular',            icon: 'ti-calendar' },
    { id: 'crm-agenda',               label: 'Agenda',               icon: 'ti-list-details' },
    { id: 'crm-prospects-open',       label: 'Open Prospects',       icon: 'ti-user-plus' },
    { id: 'crm-prospects-myopen',     label: 'My Open Prospects',    icon: 'ti-user-check' },
    { id: 'crm-prospects-closed',     label: 'Closed Prospects',     icon: 'ti-user-x' },
    // CRM ▸ Accounts ▸ Quotes node HIDDEN from the menu (owner request 2026-10-30):
    // the four crm-quotes-* leaves are dropped from the flat items (so they leave the
    // Home module card + the Ctrl+K palette) and from the children tree below (so the
    // "Quotes" folder leaves the sidebar). Their routes stay wired in AppShell and their
    // metadata is preserved via HIDDEN_NAV_ITEMS. Open Quotes is unaffected under
    // Accounting ▸ Quotes (acct-quotes-*); Prospects and Clients are untouched.
    { id: 'crm-contacts',             label: 'Contacts',             icon: 'ti-address-book' },
    { id: 'crm-clients-closed',       label: 'Closed Clients',       icon: 'ti-home-off' },
    { id: 'crm-notes-clients',        label: 'Notes in Clients',     icon: 'ti-notes' },
    { id: 'crm-notes-prospects',      label: 'Notes in Prospects',   icon: 'ti-notes' },
    { id: 'crm-unfollowed-prospects', label: 'Unfollowed Prospects', icon: 'ti-user-off' },
    { id: 'crm-unfollowed-clients',   label: 'Unfollowed Clients',   icon: 'ti-user-off' },
  ], children: [
    { kind: 'subgroup', subgroup: { id: 'sg-crm-workplace', label: 'Workplace', icon: 'ti-briefcase', children: [
      { kind: 'item', item: { id: 'crm-scheduler', label: 'Schedular', icon: 'ti-calendar' } },
      { kind: 'item', item: { id: 'crm-agenda',    label: 'Agenda',    icon: 'ti-list-details' } },
    ] } },
    { kind: 'subgroup', subgroup: { id: 'sg-crm-accounts', label: 'Accounts', icon: 'ti-users', children: [
      { kind: 'subgroup', subgroup: { id: 'sg-crm-prospects', label: 'Prospects', icon: 'ti-user-plus', children: [
        { kind: 'item', item: { id: 'crm-prospects-open',   label: 'Open Prospects',    icon: 'ti-user-plus' } },
        { kind: 'item', item: { id: 'crm-prospects-myopen', label: 'My Open Prospects', icon: 'ti-user-check' } },
        { kind: 'item', item: { id: 'crm-prospects-closed', label: 'Closed Prospects',  icon: 'ti-user-x' } },
      ] } },
      // CRM ▸ Accounts ▸ Quotes subgroup (sg-crm-quotes) HIDDEN from the sidebar
      // (owner request 2026-10-30) — see the note on the flat items above. Prospects
      // and Clients remain. To restore, re-add this subgroup here and the four
      // crm-quotes-* flat items above, and drop them from HIDDEN_NAV_ITEMS.
      // The Clients node holds Contacts + Closed Clients; the redundant "Clients" leaf
      // (crm-clients) was removed on owner request 2026-10-23 (the node itself is Clients).
      { kind: 'subgroup', subgroup: { id: 'sg-crm-clients', label: 'Clients', icon: 'ti-building', children: [
        { kind: 'item', item: { id: 'crm-contacts',       label: 'Contacts',       icon: 'ti-address-book' } },
        { kind: 'item', item: { id: 'crm-clients-closed', label: 'Closed Clients', icon: 'ti-home-off' } },
      ] } },
    ] } },
    { kind: 'subgroup', subgroup: { id: 'sg-crm-contactlogs', label: 'Contact Logs', icon: 'ti-notes', children: [
      { kind: 'item', item: { id: 'crm-notes-clients',   label: 'Notes in Clients',   icon: 'ti-notes' } },
      { kind: 'item', item: { id: 'crm-notes-prospects', label: 'Notes in Prospects', icon: 'ti-notes' } },
    ] } },
    { kind: 'subgroup', subgroup: { id: 'sg-crm-unfollowed', label: 'Unfollowed Accounts', icon: 'ti-user-off', children: [
      { kind: 'item', item: { id: 'crm-unfollowed-prospects', label: 'Unfollowed Prospects', icon: 'ti-user-off' } },
      { kind: 'item', item: { id: 'crm-unfollowed-clients',   label: 'Unfollowed Clients',   icon: 'ti-user-off' } },
    ] } },
  ] } },
  { kind: 'sep' },
  // ── Reagent Preparation — legacy ALPACA top-level module (Model.xafml Item
  //    Id="ReagentPreparation"). Shell only (2026-09-23).
  { kind: 'group', group: { id: 'grp-reagentprep', label: 'Reagent Preparation', icon: 'ti-flask', items: [
    { id: 'rgp-preplog',      label: 'Reagent Prep Log', icon: 'ti-settings' },
    { id: 'rgp-lltsearch',    label: 'LLT Search',       icon: 'ti-settings' },
    { id: 'rgp-lltdepletion', label: 'LLT Depletion',    icon: 'ti-settings' },
  ] } },
  { kind: 'sep' },
  // ─── Maintenance — mirrors ALPACA "Maintenance" group. Moved here (before User
  // & Security / Settings) to match the legacy ALPACA sidebar order (2026-09-23).
  { kind: 'group', group: { id: 'grp-maintenance', label: 'Maintenance', icon: 'ti-tool',
    items: [
      { id: 'maint-clients',         label: 'Clients',         icon: 'ti-building' },
      { id: 'maint-retired-clients', label: 'Retired Clients', icon: 'img:nav/bo-customer.png' },
      { id: 'maint-contacts',        label: 'Contacts',        icon: 'ti-address-book' },
      { id: 'maint-projects',        label: 'Project',         icon: 'ti-briefcase' },
      { id: 'maint-versionlog', label: 'Version Log', icon: 'ti-versions', iconColor: 'var(--ai-red)' },
    ],
    children: [
      // G5 Maintenance group order: Clients · Retired Clients · Contacts · Project · Security ▸ Version Log.
      { kind: 'item', item: { id: 'maint-clients',         label: 'Clients',         icon: 'ti-building' } },
      { kind: 'item', item: { id: 'maint-retired-clients', label: 'Retired Clients', icon: 'img:nav/bo-customer.png' } },
      { kind: 'item', item: { id: 'maint-contacts',        label: 'Contacts',        icon: 'ti-address-book' } },
      { kind: 'item', item: { id: 'maint-projects',        label: 'Project',         icon: 'ti-briefcase' } },
      { kind: 'subgroup', subgroup: {
          id: 'sg-maint-security', label: 'Security', icon: 'ti-shield-lock',
          children: [
            { kind: 'item', item: { id: 'maint-versionlog', label: 'Version Log', icon: 'ti-versions', iconColor: 'var(--ai-red)' } },
          ],
      }},
    ],
  } },
  { kind: 'sep' },
  // ─── User & Security — ALPACA "User and Security" group parity. Gated per item
  // by its existing ALPACA NavigationView. Moved here (after Maintenance, before
  // Settings) to match the legacy ALPACA sidebar order (2026-09-23). ───
  { kind: 'group', group: { id: 'grp-user-security', label: 'User & Security', icon: 'ti-shield-lock', items: [
    // Flat list kept for search / navMeta / pageGroupId lookups (when a group has
    // `children`, only `children` render — items must still list every leaf).
    { id: 'sec-mac',     label: 'Module Access Control', icon: 'ti-lock-cog' },
    { id: 'sec-roles',   label: 'Roles',                 icon: 'ti-id-badge-2' },
    { id: 'sec-users',   label: 'Users',                 icon: 'ti-users' },
    { id: 'sec-rolemac', label: 'Role-Based MAC',        icon: 'ti-table-options' },
    { id: 'mydetails',   label: 'My Details',            icon: 'ti-user-circle' },
    { id: 'sec-navmgr',  label: 'Navigation Manager',    icon: 'ti-list-tree' },
  ], children: [
    // GoLive parity (user 2026-09-18): "Users" nests under an "HR" folder, in the
    // legacy order Module Access Control · Roles · HR ▸ Users · Role-Based MAC ·
    // My Details (Navigation Manager is AI-only, stays last).
    { kind: 'item', item: { id: 'sec-mac',     label: 'Module Access Control', icon: 'ti-lock-cog' } },
    { kind: 'item', item: { id: 'sec-roles',   label: 'Roles',                 icon: 'ti-id-badge-2' } },
    { kind: 'subgroup', subgroup: { id: 'grp-hr', label: 'HR', icon: 'ti-users', children: [
      { kind: 'item', item: { id: 'sec-users', label: 'Users', icon: 'ti-users' } },
    ] } },
    { kind: 'item', item: { id: 'sec-rolemac', label: 'Role-Based MAC',        icon: 'ti-table-options' } },
    { kind: 'item', item: { id: 'mydetails',   label: 'My Details',            icon: 'ti-user-circle' } },
    { kind: 'item', item: { id: 'sec-navmgr',  label: 'Navigation Manager',    icon: 'ti-list-tree' } },
  ] } },
  { kind: 'sep' },
  // Settings → Reporting → Regulatory Compliance (and other future settings forms).
  // Data source: BTLIMS Windows app (Settings → DWRC-Category and Test).
  // ── Settings now appears BEFORE Data Center to match the user's preferred order.
  { kind: 'group', group: { id: 'grp-settings', label: 'Settings', icon: 'ti-settings',
    // True nested folders — matches BTLIMS pattern (Settings ▸ Accounting ▸ Payment Terms Setup):
    //   Settings ▸ Reporting ▸ Regulatory Compliance ▸ [Category | Sub Category | …]
    // User feedback 2026-05-31: "this new no need" — dropped the `badge:
    // 'new'` from every Settings menu entry.  They're no longer new.
    items: [  // legacy flat list (kept so the global nav search still finds these ids)
      { id: 'mor-template',  label: 'MOR Template Setup',       icon: 'ti-template' },
      // Settings ▸ Reporting ▸ MSR — Monthly Summary Repository config forms
      // (merged team package 2026-07-02; legacy MSRDataImportConfiguration +
      // MSRWaterSystem tables).
      { id: 'msr-data-import-config', label: 'MSR Data Import Config', icon: 'ti-file-import' },
      { id: 'msr-water-system',       label: 'Water System',           icon: 'ti-droplet' },
      { id: 'dwrc-cats',     label: 'Category',                icon: 'ti-category' },
      { id: 'dwrc-subcats',  label: 'Sub Category',            icon: 'ti-category-2' },
      { id: 'regcompliance', label: 'Category and Test Setup', icon: 'ti-shield-check' },
      // Data Package Review Checklist — Settings (legacy dbo.DataPackageCheckList).
      { id: 'data-package-checklist', label: 'Data Package Review Checklist', icon: 'ti-clipboard-check' },
      // Data Package Test View (read-only, legacy dbo.DataPackage) + DB Connection
      // (legacy dbo.DBConnection) — added 2026-09-29 (owner). Flat entries so nav search
      // finds them and the breadcrumb group resolves to "Settings".
      { id: 'set-datapackage-testview', label: 'Data Package Test View', icon: 'ti-table' },
      { id: 'set-dbconnection',         label: 'DB Connection',          icon: 'ti-database' },
      // Document Management — Document Creator (dbo.Manual master) + Retired Document
      // (read-only IsRetire=1 view). Added 2026-09-29 (owner). Flat entries so nav search
      // finds them and the breadcrumb group resolves to "Settings".
      { id: 'set-documentcreator', label: 'Document Creator', icon: 'ti-file-plus' },
      { id: 'set-retireddocument', label: 'Retired Document', icon: 'ti-file-off' },
      // Settings ▸ E-mail group (legacy XAF Settings ▸ E-mail). Added 2026-09-30 (owner).
      { id: 'set-email',                  label: 'Email',                 icon: 'ti-mail' },
      { id: 'set-email-content-template', label: 'Email Content Template', icon: 'ti-mail-cog' },
      { id: 'set-report-recipient-view',  label: 'Report Recipient View',  icon: 'ti-address-book' },
      // Settings ▸ Flutter · EDD Builder · Metrc groups (legacy XAF, added 2026-09-30 owner).
      // Flat search/navMeta catalog entries ONLY — the rendered tree lives in the sg-set-flutter
      // / sg-set-metrc subgroups + the loose EDD Builder leaf below (NOT duplicated in children).
      { id: 'set-flutter-defaultsettings',      label: 'Flutter Default Settings', icon: 'ti-settings-cog' },
      { id: 'set-flutter-fieldapptable',        label: 'Field App Table',          icon: 'ti-table' },
      { id: 'set-flutter-field-template-builder', label: 'Field Template Builder',  icon: 'ti-forms' },
      { id: 'set-flutter-template-builder',     label: 'Flutter Template Builder', icon: 'ti-forms' },
      { id: 'set-eddbuilder',                   label: 'EDD Builder',              icon: 'ti-file-export' },
      { id: 'set-metrc-incoming',               label: 'Metrc Incoming',           icon: 'ti-truck-delivery' },
      // Settings ▸ Master Data Initialization — now a GROUP with two forms (owner request 2026-10-01,
      // image 292/293): Initialization Templates + Initialization Log (both read-only lists for now).
      { id: 'set-mdi-templates',                label: 'Initialization Templates', icon: 'ti-template' },
      { id: 'set-mdi-log',                      label: 'Initialization Log',       icon: 'ti-history' },
      // Global lab workflow defaults (admin-only — visible to admin/service).
      { id: 'defaultsettings', label: 'Default Settings',      icon: 'ti-settings-cog' },
      { id: 'clientversion',   label: 'Client Version Control',  icon: 'ti-license' },
      // ALPACA's own application settings (shared dbo.AppConfig) — ported from the
      // legacy XAF Settings > Default Settings > App Config form.
      { id: 'alpacaappconfig', label: 'App Config',              icon: 'ti-adjustments-cog' },
      { id: 'screenautolock', label: 'Screen Auto Lock',        icon: 'ti-lock-clock' },
      // Settings → Default Settings → Date Filter + Report ID Format (basic ports 2026-09-29).
      { id: 'datefilter',     label: 'Date Filter',             icon: 'ti-calendar-stats' },
      { id: 'reportidformat', label: 'Report ID Format',        icon: 'ti-id-badge-2' },
      { id: 'hotkeysconfig',  label: 'Keyboard Shortcuts',      icon: 'ti-keyboard' },
      // App-wide config: client timezone + per-form date source (admin-only — ADR-0008).
      // TEMPORARY (manager decision 2026-06-15): timezone conversion is disabled
      // during coexistence, so this Date & Time config has no effect — menu entry
      // HIDDEN.  The page + route remain; restore this line when UTC is reinstated.
      // { id: 'appconfig',     label: 'App Config',              icon: 'ti-adjustments-cog' },
      // Global per-form visibility (admin-only — controls what every other
      // form's visibility switch; gated via SERVICE_ONLY in navPermissions.ts).
      { id: 'formsetup',     label: 'Form Config',              icon: 'ti-forms' },
      // Global per-form audit-trail config (admin/service) — drives the audit
      // log + reason-on-edit/delete app-wide (standing rule 11).
      { id: 'audittrailcfg', label: 'Audit Trail Configuration', icon: 'ti-history' },
      // Dashboard Designer / Dashboard Preview / Assign Dashboard To User moved to
      // System ▸ Dashboard with the ported Dashboard form (2026-09-26, ADR-0016).
      // Bottle Sharing — Settings ▸ Analysis (legacy dbo.BottleSharing).
      { id: 'set-bottlesharing', label: 'Bottle Sharing', icon: 'ti-bottle' },
      // Settings ▸ Analysis ▸ Daily QC Log Book Builder (legacy
      // DailyQCLogBookBuilder_ListView / _DetailView, ported 2026-09-23).
      { id: 'set-dailyqc-logbook', label: 'Daily QC Log Book Builder', icon: 'ti-clipboard-data' },
      // Settings ▸ Analysis ▸ PLM Reference Library — the characterised asbestos
      // reference materials + their per-analyst control limits. Ported from
      // BTLIMS/EAS ucReferenceLibrary.vb (PLM tab); ALPACA has no such form, so
      // this is AI-owned over new tenanted tables (2026-09-28).
      { id: 'set-plm-reference-library', label: 'Reference Library', icon: 'ti-microscope' },
      // Settings ▸ SDMS — table-driven calculation config (2026-07-28). The
      // Formula Library (shared dbo.SpreadSheetBuilder_Formula, used by BOTH SDMS
      // templates and Result Entry) + the Result Entry assignment per exact
      // Test/Matrix/Method combo (dbo.ResultEntry_Formula, legacy/shared table)
      // + the SDMS Template assignment (dbo.SpreadSheetBuilder_TemplateFormula,
      // legacy/shared table, 2026-09-01). Alphabetical order.
      { id: 'formulalib', label: 'Formula Library', icon: 'ti-math-symbols' },
      { id: 'reformula',  label: 'Result Entry Express Formula Setup', icon: 'ti-math-function' },
      { id: 'sdmsformula', label: 'SDMS Formula Setup', icon: 'ti-math-function' },
      { id: 'set-sdms-seqsetup', label: 'Sequence Setup', icon: 'ti-list-numbers' },
      // Settings ▸ SDMS legacy forms (owner request 2026-10-01, image 296) — read-only lists.
      { id: 'set-sdms-scientificdata',   label: 'Scientific Data Table', icon: 'ti-table' },
      { id: 'set-sdms-template-builder', label: 'SDMS Template Builder',  icon: 'ti-layout-grid' },
      { id: 'set-sdms-template-types',   label: 'Template Types',         icon: 'ti-category' },
      { id: 'set-sdms-template-formula', label: 'Template Formula',       icon: 'ti-math-function' },
      { id: 'set-sdms-formulas',         label: 'SDMS Formulas',          icon: 'ti-math-function' },
      // Custom Report Builder moved to the top-level Toolbox module (2026-09-28).
      // Settings ▸ Workflow Configuration ▸ ACT (ALPACA Custom Trigger) —
      // conditional prep-flow rules (layout preview, static data, 2026-08-13).
      { id: 'act-prepflow',  label: 'ACT',                     icon: 'ti-git-branch' },
      // Settings ▸ Workflow Configuration ▸ Workflow Engine (ADR-0011) — the
      // platform workflow service shared by all five modules.
      { id: 'workflow-engine', label: 'Workflow Engine',       icon: 'ti-sitemap' },
      // Service-only — gated via SERVICE_ONLY set in navPermissions.ts.
      // Hidden for everyone except `service` and admin users.
      { id: 'useractivity',  label: 'User Access Log',         icon: 'ti-user-check' },
      { id: 'exceptionlog',  label: 'Exception Log',           icon: 'ti-bug' },
      // Settings master-data CRUD modules (team delivery 2026-06-13).
      { id: 'set-methods',    label: 'Method',        icon: 'ti-check', iconColor: 'var(--ai-green)' },
      { id: 'set-holidays',   label: 'Holidays',      icon: 'ti-calendar-event' },
      // Settings ▸ General ▸ Qualifier Automation (legacy QualifierAutomation_ListView, read-only view).
      { id: 'set-qualifier-automation', label: 'Qualifier Automation', icon: 'ti-wand' },
      // Settings ▸ General ▸ Report Builder (legacy XAF "Report Builder" → the DevExpress Web Report Designer).
      { id: 'set-report-builder', label: 'Report Builder', icon: 'ti-report' },
      // Settings ▸ Parameter — legacy Parameter Default (Testparameter) + Parameter Library (Parameter).
      { id: 'set-parameter-default', label: 'Parameter Default', icon: 'ti-settings' },
      { id: 'set-parameter-library', label: 'Parameter Library', icon: 'ti-book' },
      // Settings ▸ Test & Method ▸ Round Off Test — bespoke rounding-rule test harness
      // (port of legacy XAF "Round Off Test", 2026-09-30). Flat entry for nav search + FormTitle.
      { id: 'set-roundofftest', label: 'Round Off Test', icon: 'ti-calculator' },
      // Settings ▸ Reagent Prep ▸ Calculation Editor — flat entry for nav search + FormTitle.
      { id: 'set-reagent-calceditor', label: 'Calculation Editor', icon: 'ti-calculator' },
      // Settings ▸ Test Pricing sub-forms — flat entries (placeholders, to be ported form-by-form).
      { id: 'set-test-default-pricing',     label: 'Default Pricing',           icon: 'ti-currency-dollar' },
      { id: 'set-contractlab-test-pricing', label: 'Contract Lab Test Pricing', icon: 'ti-currency-dollar' },
      { id: 'set-flat-test-pricing',        label: 'Flat Test Pricing',         icon: 'ti-currency-dollar' },
      { id: 'set-itemcharge-canceled',      label: 'Canceled Item Charge',      icon: 'ti-receipt' },
      { id: 'set-itemcharge-pricing',       label: 'Item Charge',               icon: 'ti-receipt' },
      { id: 'set-test-priority',            label: 'Priority',                  icon: 'ti-flag' },
      // Settings ▸ Pre-Login Settings (legacy COCSettings) — flat entry so the
      // global nav search finds it; also nested in the group tree below.
      { id: 'set-prelogin',   label: 'Pre-Login Settings', icon: 'ti-clipboard-text' },
      { id: 'set-field-tests', label: 'Field Tests',        icon: 'ti-map-pin' },
      // Settings → Instrument Management (legacy "LabwareSettings", 2026-09-18) — flat for search.
      { id: 'set-labware',             label: 'Instrument',             icon: 'ti-device-desktop-analytics' },
      { id: 'set-labware-certificate', label: 'Instrument Certificate', icon: 'ti-certificate' },
      { id: 'set-maintenance-setup',   label: 'Maintenance Setup',      icon: 'ti-tool' },
      // Settings → Inventory Management — flat for search.
      { id: 'set-inventory-items',      label: 'Items',                 icon: 'ti-package' },
      { id: 'set-vendors',              label: 'Vendors',               icon: 'ti-truck' },
      { id: 'set-assign-items-to-test', label: 'Assign Items To Test',  icon: 'ti-link' },
      { id: 'set-assign-tests-to-item', label: 'Assign Tests To Item',  icon: 'ti-link' },
      // Settings master-data lookups (merged team package 2026-07-02) — flat list
      // for global nav search (also nested under Settings ▸ Utility subgroups).
      { id: 'set-method-category',   label: 'Method Category',   icon: 'ti-category' },
      { id: 'set-qc-root-role',      label: 'QC Root Role',      icon: 'ti-hierarchy-2' },
      { id: 'set-qc-source',         label: 'QC Source',         icon: 'ti-source-code' },
      { id: 'set-sample-category',   label: 'Sample Category',   icon: 'ti-category-2' },
      { id: 'set-storage',           label: 'Storage',           icon: 'ti-archive' },
      { id: 'set-units',             label: 'Units',             icon: 'ti-ruler-2' },
      { id: 'set-report-category',   label: 'Report Category',   icon: 'ti-category' },
      { id: 'set-report-type',       label: 'Report Type',       icon: 'ti-report' },
      { id: 'set-crm-category',      label: 'Category',          icon: 'ti-category' },
      { id: 'set-request-category',  label: 'Request Category',  icon: 'ti-category' },
      { id: 'set-request-document-type', label: 'Document Type', icon: 'ti-file-description' },
      { id: 'set-request-fulfilment-method', label: 'Fulfilment Method', icon: 'ti-package' },
      { id: 'set-request-priority',   label: 'Priority',   icon: 'ti-flag' },
      { id: 'set-request-proof-type', label: 'Proof Type', icon: 'ti-signature' },
      { id: 'set-request-report-template', label: 'Report Template', icon: 'ti-file-text' },
      { id: 'set-request-sample-collection', label: 'Sample Collection', icon: 'ti-test-pipe-2' },
      { id: 'set-request-service-category', label: 'Service Category', icon: 'ti-category-2' },
      { id: 'set-request-service-type', label: 'Service Type', icon: 'ti-list-details' },
      { id: 'set-request-survey-template', label: 'Survey Template', icon: 'ti-template' },
      { id: 'set-request-turnaround', label: 'Turnaround', icon: 'ti-clock-hour-4' },
      { id: 'set-request-vehicle', label: 'Vehicle', icon: 'ti-truck' },
      { id: 'set-cmdp-sample-category',    label: 'CMDP Sample Category',        icon: 'ti-category-2' },
      { id: 'set-cmdp-method-param-match', label: 'CMDP Method and Param Match', icon: 'ti-list-check' },
      { id: 'set-asbestos-fibers',        label: 'Asbestos Fibers',        icon: 'ti-line-dashed' },
      { id: 'set-fiber-types',            label: 'Fiber Type',             icon: 'ti-line' },
      { id: 'set-fiber-type-values',      label: 'Fiber Type Values',      icon: 'ti-table' },
      { id: 'set-materials',              label: 'Materials',              icon: 'ti-box-multiple' },
      { id: 'set-non-asbestos-fibers',    label: 'Non-Asbestos Fibers',    icon: 'ti-line-dashed' },
      { id: 'set-non-fibrous-components', label: 'Non-Fibrous Components',  icon: 'ti-components' },
      { id: 'set-other-components',       label: 'Other Components',       icon: 'ti-puzzle' },
      { id: 'set-result-range',           label: 'Result Range',           icon: 'ti-arrows-horizontal' },
      { id: 'set-unconventional-analysis', label: 'Unconventional Analysis', icon: 'ti-flask-off' },
      // Contract Lab — Settings ▸ Analysis (legacy dbo.SubOutContractLab).
      { id: 'set-contractlab',            label: 'Contract Lab',           icon: 'ti-building-factory-2' },
      { id: 'acc-paymentterms', label: 'Payment Terms', icon: 'ti-cash' },
      { id: 'acc-paymentterms-setup', label: 'Payment Terms Setup', icon: 'ti-receipt' },
      { id: 'acc-region',     label: 'Region',        icon: 'ti-map-pin' },
      // Permit Types/Library/Setup "Old" removed from the nav (user request 2026-09-19).
      // Settings ▸ Permit Library New (Lifecycle v5 rebuild) — flat-catalog entry
      // so the global nav search finds it.
      { id: 'pln-permit-library', label: 'Permit Library', icon: 'ti-shield-half' },
      { id: 'pln-permit-type',    label: 'Permit Type', icon: 'ti-tags' },
      { id: 'pln-limit-type',     label: 'Limit Type',      icon: 'ti-ruler-2' },
      { id: 'pln-operators',      label: 'Operators',       icon: 'ti-math-symbols' },
      // Settings ▸ Permit Library ▸ Permit Row Colors (2026-10-21) — result-grid colour rules.
      { id: 'pln-permit-row-colors', label: 'Permit Row Colors', icon: 'ti-palette' },
      { id: 'edi-sheet-mapping',  label: 'Sheet Mapping',    icon: 'ti-table-options' },
      { id: 'edi-limits',         label: 'MCL & SCL Limits', icon: 'ti-ruler-2' },
      // System Management + Analysis Department Chain (merged 2026-06-26) — flat list for nav search.
      { id: 'adc-chain',      label: 'Analysis Department Chain', icon: 'ti-sitemap' },
      { id: 'sm-division',    label: 'Division',         icon: 'ti-building-community' },
      { id: 'sm-department',  label: 'Department',       icon: 'ti-sitemap' },
      { id: 'sm-position',    label: 'Position',         icon: 'ti-id-badge-2' },
      { id: 'sm-state',       label: 'State',            icon: 'ti-map-2' },
      { id: 'sm-country',     label: 'Country',          icon: 'ti-flag' },
      { id: 'sm-city',        label: 'City',             icon: 'ti-building' },
      { id: 'sm-language',    label: 'Current Language', icon: 'ti-language' },
      { id: 'sm-timezones',   label: 'Time Zones',       icon: 'ti-clock-hour-4' },
      // Notification Center config + browse leaves (moved under Settings 2026-06-26)
      // — kept in the flat list so global nav search still finds every nc-* id.
      ...NC_CONFIG_ITEMS, ...NC_BROWSE_ITEMS,
    ],
    children: [
      // Settings forms in ALPHABETICAL order (by label) — sub-groups sorted too.
      // Settings ▸ Accounting — Payment Terms Setup (per-client assignment grid).
      // Utility ▸ Accounting — Payment Terms + Region master-data CRUD.
      // Settings ▸ Workflow Configuration — ACT (ALPACA Custom Trigger).
      // Position here is irrelevant: the alphabetical sort below orders every
      // top-level Settings entry, and "W" lands it last.
      { kind: 'subgroup', subgroup: {
          id: 'sg-accounting', label: 'Accounting', icon: 'ti-calculator',
          children: [
            { kind: 'item', item: { id: 'acc-paymentterms-setup', label: 'Payment Terms Setup', icon: 'ti-receipt' } },
          ],
      }},
      { kind: 'subgroup', subgroup: {
          id: 'sg-workflow-config', label: 'Workflow Configuration', icon: 'ti-route',
          children: [
            { kind: 'item', item: { id: 'act-prepflow', label: 'ACT', icon: 'ti-git-branch' } },
            // Workflow Engine (ADR-0011) — the PLATFORM workflow service.  Lands
            // on the all-module catalog list (standing rule 10), from which any
            // workflow opens in the designer.  Sits beside ACT because both
            // configure process; see the ADR on the overlap with ACT's
            // conditional prep-flow rules.
            { kind: 'item', item: { id: 'workflow-engine', label: 'Workflow Engine', icon: 'ti-sitemap' } },
          ],
      }},
      // Settings ▸ Utility — parent node mirroring the legacy ALPACA "Utility"
      // navigation node (which groups Accounting, Analysis, Test, …). Built up
      // incrementally: Accounting, then Analysis (2026-09-10).
      { kind: 'subgroup', subgroup: {
          id: 'sg-utility', label: 'Utility', icon: 'ti-tools',
          children: [
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-accounting', label: 'Accounting', icon: 'ti-calculator',
                // Legacy ALPACA parity: Utility ▸ Accounting has Payment Terms
                // (the day-value lookup CRUD) and Region. Payment Terms Setup
                // (the per-client assignment grid) lives under Settings ▸ Accounting
                // (sg-accounting) matching the BTLIMS pattern.
                children: [
                  { kind: 'item', item: { id: 'acc-paymentterms', label: 'Payment Terms', icon: 'ti-cash' } },
                  { kind: 'item', item: { id: 'acc-region',       label: 'Region',        icon: 'ti-map-pin' } },
                ],
            }},
            // Analysis — moved under Utility (legacy parity, 2026-09-10). Asbestos
            // master-data lookups + Contract Lab. (Legacy also lists "Fiber Colors",
            // "PLM Sample Notes" and a "Daily Qc" subgroup — those forms don't exist
            // in AI yet, so no empty nav nodes are added for them.)
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-analysis', label: 'Analysis', icon: 'ti-microscope',
                children: [
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-asbestos', label: 'Asbestos', icon: 'ti-flask-2',
                      children: [
                        { kind: 'item', item: { id: 'set-asbestos-fibers',        label: 'Asbestos Fibers',        icon: 'ti-line-dashed' } },
                        // Fiber Colors — 2nd under Asbestos, matching legacy order (legacy FiberColors, 2026-09-10).
                        { kind: 'item', item: { id: 'set-fibercolors',            label: 'Fiber Colors',           icon: 'ti-palette' } },
                        { kind: 'item', item: { id: 'set-fiber-types',            label: 'Fiber Type',             icon: 'ti-line' } },
                        { kind: 'item', item: { id: 'set-fiber-type-values',      label: 'Fiber Type Values',      icon: 'ti-table' } },
                        { kind: 'item', item: { id: 'set-materials',              label: 'Materials',              icon: 'ti-box-multiple' } },
                        { kind: 'item', item: { id: 'set-non-asbestos-fibers',    label: 'Non-Asbestos Fibers',    icon: 'ti-line-dashed' } },
                        { kind: 'item', item: { id: 'set-non-fibrous-components', label: 'Non-Fibrous Components',  icon: 'ti-components' } },
                        { kind: 'item', item: { id: 'set-other-components',       label: 'Other Components',       icon: 'ti-puzzle' } },
                        { kind: 'item', item: { id: 'set-result-range',           label: 'Result Range',           icon: 'ti-arrows-horizontal' } },
                        { kind: 'item', item: { id: 'set-unconventional-analysis', label: 'Unconventional Analysis', icon: 'ti-flask-off' } },
                        // PLM Dispersion Oil — the RI oils the PLM Result Entry header
                        // picks from (2026-10-08); directly before PLM Sample Notes, as
                        // the lab's own menu has it.
                        { kind: 'item', item: { id: 'set-plm-dispersion-oil',     label: 'PLM Dispersion Oil',     icon: 'ti-droplet' } },
                        // PLM Instrument Setup — the station devices (G5 parity 2026-10-15).
                        { kind: 'item', item: { id: 'set-plm-instrument-setup',   label: 'PLM Instrument Setup',   icon: 'ti-microscope' } },
                        // PLM Sample Notes — LAST under Asbestos, matching the legacy
                        // ALPACA order (legacy PLMSampleComments, 2026-09-10).
                        { kind: 'item', item: { id: 'set-plm-sample-notes',       label: 'PLM Sample Notes',       icon: 'ti-note' } },
                      ],
                  }},
                  // Daily Qc — legacy DailyQcDataTable + DailyQCCategory (2026-09-10).
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-dailyqc', label: 'Daily Qc', icon: 'ti-calendar-stats',
                      children: [
                        { kind: 'item', item: { id: 'set-dailyqc-datatable', label: 'Data Table', icon: 'ti-table' } },
                        { kind: 'item', item: { id: 'set-dailyqc-category',  label: 'Category',   icon: 'ti-category' } },
                      ],
                  }},
                  // Contract Lab removed from Analysis (2026-09-10, user request) —
                  // it is not part of the legacy Utility ▸ Analysis structure.
                ],
            }},
            // Audit Trail ▸ Reason for Change (legacy AuditTrailReasontoChange, 2026-09-10).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-audittrail', label: 'Audit Trail', icon: 'ti-history',
                children: [
                  { kind: 'item', item: { id: 'set-reasonforchange', label: 'Reason for Change', icon: 'ti-edit-circle' } },
                ],
            }},
            // Client + Client Account Number (legacy dbo.Client / dbo.ClientAccountNumber, 2026-09-10).
            { kind: 'item', item: { id: 'set-client',            label: 'Client',                icon: 'ti-user' } },
            { kind: 'item', item: { id: 'set-clientaccountnumber', label: 'Client Account Number', icon: 'ti-id' } },
            // Client Request ▸ Request Category — moved under Utility (legacy parity, 2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-clientreq', label: 'Client Request', icon: 'ti-mail-forward',
                children: [
                  { kind: 'item', item: { id: 'set-request-category', label: 'Request Category', icon: 'ti-category' } },
                  // Document Type — the Document Pickup "Document type" options
                  // (dbo.AI_ClientRequestDocumentType, 2026-09-18).
                  { kind: 'item', item: { id: 'set-request-document-type', label: 'Document Type', icon: 'ti-file-description' } },
                  // Fulfilment Method — the Bottle Order "Fulfilment method" options
                  // (dbo.AI_ClientRequestFulfilmentMethod, 2026-09-18).
                  { kind: 'item', item: { id: 'set-request-fulfilment-method', label: 'Fulfilment Method', icon: 'ti-package' } },
                  // Priority (dbo.AI_ClientRequestPriority) moved to the General subgroup
                  // (owner request 2026-09-30).
                  // Proof Type — the Delivery "Proof required" options
                  // (dbo.AI_ClientRequestProofType, 2026-09-18).
                  { kind: 'item', item: { id: 'set-request-proof-type', label: 'Proof Type', icon: 'ti-signature' } },
                  // Report Template — the Service Task Report template names
                  // (dbo.AI_ClientRequestReportTemplate, 2026-09-17).
                  { kind: 'item', item: { id: 'set-request-report-template', label: 'Report Template', icon: 'ti-file-text' } },
                  // Sample Collection — the Survey "Sample collection" options
                  // (dbo.AI_ClientRequestSampleCollection, 2026-09-18).
                  { kind: 'item', item: { id: 'set-request-sample-collection', label: 'Sample Collection', icon: 'ti-test-pipe-2' } },
                  // Client Request Service Category — the five service categories
                  // (dbo.AI_ClientRequestServiceCategory, 2026-09-17).
                  { kind: 'item', item: { id: 'set-request-service-category', label: 'Service Category', icon: 'ti-category-2' } },
                  // Client Request Service Type — the Service type options per service
                  // category (dbo.AI_ClientRequestServiceType, 2026-09-17).
                  { kind: 'item', item: { id: 'set-request-service-type', label: 'Service Type', icon: 'ti-list-details' } },
                  // Client Request Survey Template — the Survey "Survey template" options
                  // (dbo.AI_ClientRequestSurveyTemplate, 2026-09-17).
                  { kind: 'item', item: { id: 'set-request-survey-template', label: 'Survey Template', icon: 'ti-template' } },
                  // Client Request Turnaround — the values the Turnaround dropdown offers
                  // (Mobile Lab · Same Day · Next Day), maintainable since 2026-09-17.
                  { kind: 'item', item: { id: 'set-request-turnaround', label: 'Turnaround', icon: 'ti-clock-hour-4' } },
                  // Vehicle — the vehicles a route can be driven in
                  // (dbo.AI_ClientRequestVehicle, 2026-09-18).
                  { kind: 'item', item: { id: 'set-request-vehicle', label: 'Vehicle', icon: 'ti-truck' } },
                ],
            }},
            // CRM ▸ Category — moved under Utility (legacy parity, 2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-crm', label: 'CRM', icon: 'ti-address-book',
                children: [
                  { kind: 'item', item: { id: 'set-crm-category', label: 'Category', icon: 'ti-category' } },
                ],
            }},
            // CMDP — moved under Utility (legacy parity, 2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-cmdp', label: 'CMDP', icon: 'ti-clipboard-data',
                children: [
                  { kind: 'item', item: { id: 'set-cmdp-sample-category',     label: 'CMDP Sample Category',      icon: 'ti-category-2' } },
                  { kind: 'item', item: { id: 'set-cmdp-method-param-match',  label: 'CMDP Method and Param Match', icon: 'ti-list-check' } },
                ],
            }},
            // Custom Report Builder — moved under Utility (legacy parity, 2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-crb', label: 'Custom Report Builder', icon: 'ti-layout-grid',
                children: [
                  { kind: 'item', item: { id: 'set-report-category', label: 'Report Category', icon: 'ti-category' } },
                  { kind: 'item', item: { id: 'set-report-type',     label: 'Report Type',     icon: 'ti-report' } },
                ],
            }},
            // e-Data Transfer — legacy dbo.InstrumentSoftware / ParameterMatch /
            // QCTypeMatch / ICP_MS_MassPick (2026-09-11, new build).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-edata', label: 'e-Data Transfer', icon: 'ti-transfer',
                children: [
                  { kind: 'item', item: { id: 'set-parsersetup',    label: 'Parser Setup',      icon: 'ti-plug-connected' } },
                  { kind: 'item', item: { id: 'set-parametermatch', label: 'Parameter Match',   icon: 'ti-arrows-join' } },
                  { kind: 'item', item: { id: 'set-qctypematch',    label: 'QC Type Match',     icon: 'ti-arrows-join-2' } },
                  { kind: 'item', item: { id: 'set-icpmsmasspick',  label: 'ICP-MS Mass Pick',  icon: 'ti-atom' } },
                ],
            }},
            // Document Management → Document → Category (legacy DocumentCategory, 2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-docmgmt', label: 'Document Management', icon: 'ti-files',
                children: [
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-doc', label: 'Document', icon: 'ti-file',
                      children: [
                        { kind: 'item', item: { id: 'set-documentcategory', label: 'Category', icon: 'ti-category' } },
                      ],
                  }},
                ],
            }},
            // EDD Builder → Category (legacy EDDCategory, 2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-eddbuilder', label: 'EDD Builder', icon: 'ti-file-export',
                children: [
                  { kind: 'item', item: { id: 'set-eddcategory', label: 'Category', icon: 'ti-category' } },
                ],
            }},
            // Glossary → Category (legacy GlossaryCategory, 2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-glossary', label: 'Glossary', icon: 'ti-book',
                children: [
                  { kind: 'item', item: { id: 'set-glossarycategory', label: 'Category', icon: 'ti-category' } },
                ],
            }},
            // Internal Audit → NCA → Action Category / NCA Reason / Problem Category
            // (legacy ActionCategory / NCAReason / ProblemCategory, 2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-internalaudit', label: 'Internal Audit', icon: 'ti-clipboard-check',
                children: [
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-nca', label: 'NCA', icon: 'ti-alert-triangle',
                      children: [
                        { kind: 'item', item: { id: 'set-actioncategory',  label: 'Action Category',  icon: 'ti-category' } },
                        { kind: 'item', item: { id: 'set-ncareason',       label: 'NCA Reason',       icon: 'ti-message-report' } },
                        { kind: 'item', item: { id: 'set-problemcategory', label: 'Problem Category', icon: 'ti-category' } },
                      ],
                  }},
                ],
            }},
            // Inventory Management (ICM) simple lookup masters — under Utility (legacy order).
            // The main Inventory Management group (Items, Vendors, etc.) lives at Settings level.
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-inventory', label: 'Inventory Management', icon: 'ti-packages',
                children: [
                  { kind: 'item', item: { id: 'set-delivery-priority',       label: 'Delivery Priority',       icon: 'ti-flag' } },
                  { kind: 'item', item: { id: 'set-grade',                   label: 'Grade',                   icon: 'ti-award' } },
                  { kind: 'item', item: { id: 'set-item-categories',         label: 'Item Categories',         icon: 'ti-category' } },
                  { kind: 'item', item: { id: 'set-manufacturers',           label: 'Manufacturers',           icon: 'ti-building-factory' } },
                  { kind: 'item', item: { id: 'set-package-units',           label: 'Package Units',           icon: 'ti-package' } },
                  { kind: 'item', item: { id: 'set-icm-project',             label: 'Project',                 icon: 'ti-folder' } },
                  { kind: 'item', item: { id: 'set-shipping-options',        label: 'Shipping Options',        icon: 'ti-truck-delivery' } },
                  { kind: 'item', item: { id: 'set-ship-via',                label: 'Ship via',                icon: 'ti-truck' } },
                  { kind: 'item', item: { id: 'set-icm-storage',             label: 'Storage',                 icon: 'ti-building-warehouse' } },
                  { kind: 'item', item: { id: 'set-vendor-evaluation-items', label: 'Vendor Evaluation Items', icon: 'ti-clipboard-list' } },
                ],
            }},
            // Maintenance → Security → Version Log → Deployment Team
            // (legacy DeploymentTeam, 2026-09-11). Comes before Inspection in legacy.
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-maintenance', label: 'Maintenance', icon: 'ti-tool',
                children: [
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-maint-security', label: 'Security', icon: 'ti-shield-lock',
                      children: [
                        { kind: 'subgroup', subgroup: {
                            id: 'sg-util-maint-versionlog', label: 'Version Log', icon: 'ti-versions',
                            children: [
                              { kind: 'item', item: { id: 'set-deployment-team', label: 'Deployment Team', icon: 'ti-users-group' } },
                            ],
                        }},
                      ],
                  }},
                ],
            }},
            // Inspection → Inspection Category (legacy InspectCategory, 2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-inspection', label: 'Inspection', icon: 'ti-zoom-check',
                children: [
                  { kind: 'item', item: { id: 'set-inspection-category', label: 'Inspection Category', icon: 'ti-category' } },
                ],
            }},
            // Permit — the AI Lifecycle-v5 lookups (Limit Type + Operators). Permit Type moved to
            // the Settings ▸ Permit Library node (owner request 2026-09-30, legacy structure).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-permit', label: 'Permit', icon: 'ti-shield-half',
                children: [
                  // Limit Type — the Lifecycle v5 limit types (MCL, SMCL, AL, ...).
                  { kind: 'item', item: { id: 'pln-limit-type', label: 'Limit Type', icon: 'ti-ruler-2' } },
                  // Operators — read-only reference list of the comparison operators a
                  // Permit Limit can use (Lifecycle v5 prototype).
                  { kind: 'item', item: { id: 'pln-operators', label: 'Operators', icon: 'ti-math-symbols' } },
                ],
            }},
            // Prep Options — legacy top-level Utility leaf (PrepOptions, 2026-09-11).
            { kind: 'item', item: { id: 'set-prep-options', label: 'Prep Options', icon: 'ti-test-pipe' } },
            // Project Category — legacy top-level Utility leaf (ProjectCategory, 2026-09-11).
            { kind: 'item', item: { id: 'set-project-category', label: 'Project Category', icon: 'ti-folders' } },
            // Instrument (legacy Labware group) — renders AFTER Project Category in
            // the legacy nav. Instrument Category + Maintenance Setup sub-menu
            // (Category · Freq Code · Service Company · Skip Reason · Task
            // Checklist · Task Ownership) (2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-instrument', label: 'Instrument', icon: 'ti-microscope',
                children: [
                  { kind: 'item', item: { id: 'set-instrument-category', label: 'Instrument Category', icon: 'ti-category' } },
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-maint-setup', label: 'Maintenance Setup', icon: 'ti-tool',
                      children: [
                        { kind: 'item', item: { id: 'set-maintenance-category', label: 'Category',         icon: 'ti-category' } },
                        { kind: 'item', item: { id: 'set-freq-code',           label: 'Freq Code',        icon: 'ti-clock-hour-4' } },
                        { kind: 'item', item: { id: 'set-skip-reason',          label: 'Skip Reason',      icon: 'ti-player-skip-forward' } },
                        { kind: 'item', item: { id: 'set-service-company',      label: 'Service Company',  icon: 'ti-building' } },
                        { kind: 'item', item: { id: 'set-task-checklist',       label: 'Task Checklist',   icon: 'ti-checklist' } },
                        { kind: 'item', item: { id: 'set-task-ownership',       label: 'Task Ownership',   icon: 'ti-user-check' } },
                      ],
                  }},
                ],
            }},
            // Routes — legacy top-level Utility leaf, renders after Instrument (2026-09-11).
            { kind: 'item', item: { id: 'set-routes', label: 'Routes', icon: 'ti-route' } },
            // Sample Sites — legacy top-level Utility leaf (SampleSites), after Routes (2026-09-11).
            { kind: 'item', item: { id: 'set-sample-sites', label: 'Sample Sites', icon: 'ti-map-pin' } },
            // QA/QC — PT Study Log (Study ID · Study Name · Source) + QC Category (2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-qaqc', label: 'QA/QC', icon: 'ti-shield-check',
                children: [
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-ptstudylog', label: 'PT Study Log', icon: 'ti-clipboard-data',
                      children: [
                        { kind: 'item', item: { id: 'set-study-id',   label: 'Study ID',   icon: 'ti-id' } },
                        { kind: 'item', item: { id: 'set-study-name', label: 'Study Name', icon: 'ti-tag' } },
                        { kind: 'item', item: { id: 'set-source',     label: 'Source',     icon: 'ti-source-code' } },
                      ],
                  }},
                  { kind: 'item', item: { id: 'set-qc-category', label: 'QC Category', icon: 'ti-category' } },
                ],
            }},
            // Reagent Prep — 6 masters (2026-09-11).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-reagentprep', label: 'Reagent Prep', icon: 'ti-flask-2',
                children: [
                  { kind: 'item', item: { id: 'set-calculation-approach', label: 'Calculation Approach', icon: 'ti-calculator' } },
                  { kind: 'item', item: { id: 'set-reagent-operator',     label: 'Operator',             icon: 'ti-user-cog' } },
                  { kind: 'item', item: { id: 'set-standard-name',        label: 'Standard Name',        icon: 'ti-tag' } },
                  { kind: 'item', item: { id: 'set-reagent-units',        label: 'Units',                icon: 'ti-ruler-2' } },
                  { kind: 'item', item: { id: 'set-ws-prep-type',         label: 'WS Prep Type',         icon: 'ti-category-2' } },
                  { kind: 'item', item: { id: 'set-ws-storage-name',      label: 'WS Storage Name',      icon: 'ti-building-warehouse' } },
                ],
            }},
            // Reporting — CoverPage NELAP + MSR. Legacy GoLive shows MSR with exactly
            // two nodes: MSR Data Import Config + Water System (no "MSR Categories"
            // node), so we reuse those two ids (merged MSR package, 2026-07-02) and
            // do NOT surface set-msr-category here (corrected 2026-09-12 per legacy).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-reportingsettings', label: 'Reporting', icon: 'ti-report',
                children: [
                  { kind: 'item', item: { id: 'set-coverpage-nelap', label: 'CoverPage NELAP', icon: 'ti-file-certificate' } },
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-msr', label: 'MSR', icon: 'ti-droplet',
                      children: [
                        { kind: 'item', item: { id: 'msr-data-import-config', label: 'MSR Data Import Config', icon: 'ti-file-import' } },
                        { kind: 'item', item: { id: 'msr-water-system',       label: 'Water System',          icon: 'ti-droplet-filled' } },
                      ],
                  }},
                ],
            }},
            // Workflow Status Chain — legacy dbo.StatusDefinition (StatusDefinition_ListView,
            // Criteria "[BO] Is Not Null"). Top-level Utility leaf, renders after Reporting
            // in the legacy rendered order (2026-09-12).
            { kind: 'item', item: { id: 'set-workflow-status-chain', label: 'Workflow Status Chain', icon: 'ti-arrows-split-2' } },
            // Sample Tracking — legacy Utility group (renders after Workflow Status Chain).
            // Legacy structure: Collectors (a subgroup that nests the Collectors form) +
            // Sample Registration (subgroup). Building node-by-node.
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-sampletracking', label: 'Sample Tracking', icon: 'ti-clipboard-list',
                children: [
                  // Collectors — legacy nests the Collectors form under a "Collectors" group.
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-st-collectors', label: 'Collectors', icon: 'ti-users',
                      children: [
                        { kind: 'item', item: { id: 'set-collectors', label: 'Collectors', icon: 'ti-users' } },
                      ],
                  }},
                  // Sample Registration — legacy group, all 7 nodes in legacy order:
                  // Check Points Setup · Sample Matrix Field Setup · Sample Field
                  // Configuration · eNotification Content Template · Payment Status ·
                  // Job Inactivation · QC Category. Sample Field Configuration + QC
                  // Category reuse ids shown elsewhere (legacy repeats them under
                  // Test / QA-QC). Sample Matrix Field Setup + eNotification Content
                  // Template are list-only for now (detail port pending) (2026-09-12).
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-st-samplereg', label: 'Sample Registration', icon: 'ti-clipboard-plus',
                      children: [
                        { kind: 'item', item: { id: 'set-check-points-setup',       label: 'Check Points Setup',           icon: 'ti-checklist' } },
                        { kind: 'item', item: { id: 'set-sample-matrix-field-setup', label: 'Sample Matrix Field Setup',    icon: 'ti-table-options' } },
                        { kind: 'item', item: { id: 'set-samplefieldconfig',        label: 'Sample Field Configuration',   icon: 'ti-forms' } },
                        { kind: 'item', item: { id: 'set-enotification-template',    label: 'eNotification Content Template', icon: 'ti-mail-cog' } },
                        { kind: 'item', item: { id: 'set-payment-status',           label: 'Payment Status',               icon: 'ti-credit-card' } },
                        { kind: 'item', item: { id: 'set-job-inactivation',         label: 'Job Inactivation',             icon: 'ti-ban' } },
                        { kind: 'item', item: { id: 'set-qc-category',              label: 'QC Category',                  icon: 'ti-category' } },
                      ],
                  }},
                ],
            }},
            // Sample Sites lookup group — legacy Utility Index 27 (distinct from the
            // Sample Sites FORM leaf above, Index 25). The 5 lookups behind the Sample
            // Sites dropdowns: PWS ID · PWS Name · Sample Type · System Type · Water
            // Type (legacy order). Renders right after Sample Tracking (2026-09-12).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-samplesites-lookups', label: 'Sample Sites', icon: 'ti-map-pins',
                children: [
                  { kind: 'item', item: { id: 'set-pws-id',      label: 'PWS ID',      icon: 'ti-id-badge-2' } },
                  { kind: 'item', item: { id: 'set-pws-name',    label: 'PWS Name',    icon: 'ti-tag' } },
                  { kind: 'item', item: { id: 'set-sample-type', label: 'Sample Type', icon: 'ti-test-pipe' } },
                  { kind: 'item', item: { id: 'set-system-type', label: 'System Type', icon: 'ti-settings-2' } },
                  { kind: 'item', item: { id: 'set-water-type',  label: 'Water Type',  icon: 'ti-droplet' } },
                ],
            }},
            // Sample Login lookup group — legacy Utility Index 28 (renders after the
            // Sample Sites lookup group). Children are in the RENDERED legacy order
            // (from the GoLive nav, NOT the Model.xafml Index attribute — they differ):
            // Bare · Rejection Criteria · Sample Status · Code · Kind · Component ·
            // Site Source · Repeat Location · Sanitarian · Sanitarian Temperature ·
            // Size · Source · User Defined Dropdowns · AirVolume · Total Surface Area.
            // Source reuses the QA/QC Source form; legacy "Temp" is Visible=False,
            // omitted (order corrected 2026-09-16 per user screenshot).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-samplelogin', label: 'Sample Login', icon: 'ti-login',
                children: [
                  { kind: 'item', item: { id: 'set-sl-bare',                label: 'Bare',                  icon: 'ti-cylinder' } },
                  { kind: 'item', item: { id: 'set-rejection-criteria',     label: 'Rejection Criteria',    icon: 'ti-ban' } },
                  { kind: 'item', item: { id: 'set-sample-status',          label: 'Sample Status',         icon: 'ti-flag' } },
                  { kind: 'item', item: { id: 'set-sl-code',                label: 'Code',                  icon: 'ti-code' } },
                  { kind: 'item', item: { id: 'set-sl-kind',                label: 'Kind',                  icon: 'ti-tag' } },
                  { kind: 'item', item: { id: 'set-sl-component',           label: 'Component',             icon: 'ti-components' } },
                  { kind: 'item', item: { id: 'set-site-source',            label: 'Site Source',           icon: 'ti-source-code' } },
                  { kind: 'item', item: { id: 'set-repeat-location',        label: 'Repeat Location',       icon: 'ti-repeat' } },
                  { kind: 'item', item: { id: 'set-sl-sanitarian',          label: 'Sanitarian',            icon: 'ti-user-shield' } },
                  { kind: 'item', item: { id: 'set-sanitarian-temperature', label: 'Sanitarian Temperature', icon: 'ti-temperature' } },
                  { kind: 'item', item: { id: 'set-sl-size',                label: 'Size',                  icon: 'ti-ruler-2' } },
                  { kind: 'item', item: { id: 'set-source',                 label: 'Source',                icon: 'ti-source-code' } },
                  { kind: 'item', item: { id: 'set-user-defined-dropdown',  label: 'User Defined Dropdowns', icon: 'ti-list' } },
                  { kind: 'item', item: { id: 'set-sl-air-volume',          label: 'AirVolume',             icon: 'ti-wind' } },
                  { kind: 'item', item: { id: 'set-sl-total-surface-area',  label: 'Total Surface Area',    icon: 'ti-square' } },
                ],
            }},
            // Topic — legacy top-level Utility leaf (dbo.Topic), renders after the
            // Sample Login group in the GoLive nav (2026-09-16).
            { kind: 'item', item: { id: 'set-topic', label: 'Topic', icon: 'ti-notes' } },
            // Subout Tracking — legacy Utility group (Index 29), renders after Topic
            // in the GoLive nav. The two sub-lab parameter-match lookups (2026-09-17).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-subout', label: 'Subout Tracking', icon: 'ti-transfer',
                children: [
                  { kind: 'item', item: { id: 'set-sublabparammatch',   label: 'Sub Lab Parameter Match',    icon: 'ti-arrows-transfer-down' } },
                  { kind: 'item', item: { id: 'set-sublabqcparammatch', label: 'Sub Lab Qc Parameter Match', icon: 'ti-arrows-transfer-up' } },
                ],
            }},
            // Test Pricing — legacy Utility group (Index 30), renders after Subout
            // Tracking. Its only view is Item Charge Pricing → Category (2026-09-17).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-testpricing', label: 'Test Pricing', icon: 'ti-tag',
                children: [
                  { kind: 'subgroup', subgroup: {
                      id: 'sg-util-itemchargepricing', label: 'Item Charge Pricing', icon: 'ti-receipt',
                      children: [
                        { kind: 'item', item: { id: 'set-itemchargepricing-category', label: 'Category', icon: 'ti-category' } },
                      ],
                  }},
                ],
            }},
            // Settings ▸ Utility ▸ Test (legacy Utility group Index 31, renders right
            // after Test Pricing). Moved INTO Utility 2026-09-17 (was a Settings-level
            // sibling, so it did not appear under Utility where GoLive shows it).
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-test', label: 'Test', icon: 'ti-flask',
          // Ordered to the legacy rendered Test nav (2026-09-17). Complex bespoke
          // views (Container Settings, Instruments ×3, Tab Field Configuration) slot
          // in at their legacy positions in Stage 2.
          children: [
            { kind: 'item', item: { id: 'set-accreditation',     label: 'Accreditation',    icon: 'ti-certificate' } },
            { kind: 'item', item: { id: 'set-broughtby',         label: 'Relinquished By',  icon: 'ti-user-up' } },
            { kind: 'item', item: { id: 'set-container',         label: 'Container',        icon: 'ti-box' } },
            { kind: 'item', item: { id: 'set-container-settings', label: 'Container Settings', icon: 'ti-adjustments-cog' } },
            { kind: 'item', item: { id: 'set-holdingtime',       label: 'Holding Time',     icon: 'ti-clock' } },
            { kind: 'item', item: { id: 'set-instruments-field',      label: 'Instruments, Field Monitoring', icon: 'ti-device-analytics' } },
            { kind: 'item', item: { id: 'set-instruments-lab',        label: 'Instruments, Lab Analysis',     icon: 'ti-microscope' } },
            { kind: 'item', item: { id: 'set-instruments-sampleprep', label: 'Instruments, Sample Prep',      icon: 'ti-flask-2' } },
            { kind: 'item', item: { id: 'set-matrix',            label: 'Matrix',           icon: 'ti-grid-dots' } },
            { kind: 'item', item: { id: 'set-method-category',   label: 'Method Category',  icon: 'ti-category' } },
            { kind: 'item', item: { id: 'set-preptypes',         label: 'Prep Types',       icon: 'ti-test-pipe' } },
            { kind: 'item', item: { id: 'set-preservative',      label: 'Preservative',     icon: 'ti-droplet' } },
            { kind: 'item', item: { id: 'set-preservecondition', label: 'Preserve Condition', icon: 'ti-snowflake' } },
            { kind: 'item', item: { id: 'set-temperature',       label: 'Temperature',      icon: 'ti-temperature' } },
            { kind: 'item', item: { id: 'set-qcrole',            label: 'QC Role',          icon: 'ti-user-cog' } },
            { kind: 'item', item: { id: 'set-qc-root-role',      label: 'QC Root Role',     icon: 'ti-hierarchy-2' } },
            { kind: 'item', item: { id: 'set-qc-source',         label: 'QC Source',        icon: 'ti-source-code' } },
            { kind: 'item', item: { id: 'set-qctype',            label: 'QC Type',          icon: 'ti-checkbox' } },
            { kind: 'item', item: { id: 'set-sample-category',   label: 'Sample Category',  icon: 'ti-category-2' } },
            { kind: 'item', item: { id: 'set-sample-matrix',     label: 'Sample Matrix',    icon: 'ti-grid-pattern' } },
            // Sample Field Configuration (legacy VisualMatrix field config) —
            // matrix → per-tab available↔selected fields (2026-08-25). AI-extra
            // VisualMatrix view, kept beside Sample Matrix.
            { kind: 'item', item: { id: 'set-samplefieldconfig', label: 'Sample Field Configuration', icon: 'ti-forms' } },
            { kind: 'item', item: { id: 'set-storage',           label: 'Storage',          icon: 'ti-archive' } },
            { kind: 'subgroup', subgroup: {
                id: 'sg-util-testfieldconfig', label: 'Test Field Configuration', icon: 'ti-adjustments',
                children: [
                  { kind: 'item', item: { id: 'set-tabcontrols', label: 'Tab Controls', icon: 'ti-columns' } },
                  { kind: 'item', item: { id: 'set-tabfieldconfig', label: 'Tab Field Configuration', icon: 'ti-forms' } },
                ],
            }},
            { kind: 'item', item: { id: 'set-units',             label: 'Units',            icon: 'ti-ruler-2' } },
                ],
            }},
            // UnFollow Settings — legacy Utility leaf Index 35, renders after Test
            // (singleton settings record; StatusDefinition/Index 36 = Workflow Status
            // Chain, already built earlier in the rendered order). (2026-09-17)
            { kind: 'item', item: { id: 'set-unfollow-settings', label: 'UnFollow Settings', icon: 'ti-user-off' } },
          ],
      }},
      // (Analysis moved under Settings ▸ Utility ▸ Analysis — see the sg-utility node above. 2026-09-10.)
      // (Custom Report Builder moved under Settings ▸ Utility — see the sg-utility node above. 2026-09-11.)
      // (CMDP moved under Settings ▸ Utility — see the sg-utility node above. 2026-09-11.)
      // (CRM moved under Settings ▸ Utility — see the sg-utility node above. 2026-09-11.)
      // (Client Request moved under Settings ▸ Utility — see the sg-utility node above. 2026-09-11.)
      // ── Settings regroup (2026-09-24, Selvi) ─────────────────────────────
      // 17 forms used to sit LOOSE directly under Settings while everything else
      // was in a folder, which made the tree look uneven. They are now grouped
      // below, so every Settings entry is a folder. This is navigation-only —
      // no form is added, removed or renamed and every id is unchanged, so Form
      // Config (whose catalog is derived from this tree) and all per-user
      // permissions are unaffected. The flat `items` list above is untouched, so
      // global nav search still finds each form.
      // Two of the 17 went into EXISTING folders instead of a new one:
      //   · Data Package Review Checklist → SDMS   (2026-09-24; MOVED again to its own
      //     "Data Package" folder on 2026-09-29 per owner — see sg-set-datapackage below)
      //   · Custom Report Builder         → Reporting
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-audit-logs', label: 'Audit & Logs', icon: 'ti-history',
          children: [
            { kind: 'item', item: { id: 'audittrailcfg', label: 'Audit Trail Configuration', icon: 'ti-history' } },
            { kind: 'item', item: { id: 'useractivity',  label: 'User Access Log',           icon: 'ti-user-check' } },
            { kind: 'item', item: { id: 'exceptionlog',  label: 'Exception Log',             icon: 'ti-bug' } },
          ],
      }},
      // Data Package — QC data-package review forms. Data Package Review Checklist was
      // moved here from SDMS (owner request 2026-09-29), reversing the 2026-09-24 SDMS
      // placement; it is the node's only member for now. Settings subgroups render
      // alphabetically (sortTopLevel), so this lands right after "Audit & Logs".
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-datapackage', label: 'Data Package', icon: 'ti-package',
          children: [
            { kind: 'item', item: { id: 'data-package-checklist',   label: 'Data Package Review Checklist', icon: 'ti-clipboard-check' } },
            // Data Package Test View (read-only) + DB Connection added 2026-09-29 (owner) —
            // the two forms that were missing from the new project (legacy ALPACA nav order).
            { kind: 'item', item: { id: 'set-datapackage-testview', label: 'Data Package Test View',        icon: 'ti-table' } },
            { kind: 'item', item: { id: 'set-dbconnection',         label: 'DB Connection',                 icon: 'ti-database' } },
          ],
      }},
      // Document Management — the Settings-level document master group (owner request
      // 2026-09-29). Document Creator (dbo.Manual master, editable) + Retired Document
      // (read-only IsRetire=1 view). Settings subgroups render alphabetically
      // (sortTopLevel), so this lands at its "Do…" slot — a couple of nodes below
      // "Data Package"/"Default Settings", not literally adjacent to Data Package.
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-document-mgmt', label: 'Document Management', icon: 'ti-folder',
          children: [
            { kind: 'item', item: { id: 'set-documentcreator', label: 'Document Creator', icon: 'ti-file-plus' } },
            { kind: 'item', item: { id: 'set-retireddocument', label: 'Retired Document', icon: 'ti-file-off' } },
          ],
      }},
      // Settings ▸ E-mail — legacy XAF Settings ▸ E-mail group (owner request 2026-09-30).
      // Email + Email Content Template are CRUD masters; Report Recipient View is a
      // read-only Customer list (customers flagged fEmail=1).
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-email', label: 'E-mail', icon: 'ti-mail',
          children: [
            { kind: 'item', item: { id: 'set-email',                  label: 'Email',                  icon: 'ti-mail' } },
            { kind: 'item', item: { id: 'set-email-content-template', label: 'Email Content Template',  icon: 'ti-mail-cog' } },
            { kind: 'item', item: { id: 'set-report-recipient-view',  label: 'Report Recipient View',   icon: 'ti-address-book' } },
          ],
      }},
      // Settings ▸ Field Template — moved OUT of Flutter to its own top-level group (owner request
      // 2026-09-30). Renders alphabetically BEFORE "Flutter". Holds the Flutter field-template
      // stubs (Field App Table, Field Template Builder) plus the Field Tests form.
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-fieldtemplate', label: 'Field Template', icon: 'ti-template',
          children: [
            { kind: 'item', item: { id: 'set-flutter-fieldapptable',        label: 'Field App Table',        icon: 'ti-table' } },
            { kind: 'item', item: { id: 'set-flutter-field-template-builder', label: 'Field Template Builder', icon: 'ti-forms' } },
            { kind: 'item', item: { id: 'set-field-tests',                   label: 'Field Tests',            icon: 'ti-map-pin' } },
          ],
      }},
      // Settings ▸ Flutter — legacy XAF Settings ▸ Flutter group (owner request 2026-09-30).
      // Flutter Default Settings is a singleton config (six behaviour toggles over the shared
      // dbo.FlutterDefaultSettings); Flutter Template Builder is a READ-ONLY list stub (the
      // interactive builder needs the legacy source per §18.11b). Field Template moved out (above).
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-flutter', label: 'Flutter', icon: 'ti-device-mobile',
          children: [
            { kind: 'item', item: { id: 'set-flutter-defaultsettings',   label: 'Flutter Default Settings', icon: 'ti-settings-cog' } },
            { kind: 'item', item: { id: 'set-flutter-template-builder',  label: 'Flutter Template Builder', icon: 'ti-forms' } },
          ],
      }},
      // Settings ▸ EDD — new group next to E-mail holding the EDD Builder form (owner request
      // 2026-09-30). Renders alphabetically right after "E-mail". EDD Builder is a READ-ONLY list
      // stub over dbo.EDDBuilder; the interactive builder awaits the ALPACA source (§18.11b).
      // Category lives separately under Utility ▸ EDD Builder ▸ Category.
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-edd', label: 'EDD', icon: 'ti-file-export',
          children: [
            { kind: 'item', item: { id: 'set-eddbuilder', label: 'EDD Builder', icon: 'ti-file-export' } },
          ],
      }},
      // Settings ▸ METRC — legacy XAF Settings ▸ METRC (owner request 2026-09-30). Metrc
      // Incoming is a READ-ONLY history stub over dbo.MetrcIncomingHistory; the live METRC
      // state-API integration awaits the ALPACA source (§18.11b).
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-metrc', label: 'Metrc', icon: 'ti-plant',
          children: [
            { kind: 'item', item: { id: 'set-metrc-incoming', label: 'Metrc Incoming', icon: 'ti-truck-delivery' } },
          ],
      }},
      // Settings ▸ Master Data Initialization — now a GROUP (owner request 2026-10-01, image 292/293),
      // with the two legacy child forms: Initialization Templates (MasterDataInitialization_ListView) and
      // Initialization Log (InitializationLog_ListView). Both are read-only lists for now (the tables are
      // empty; the full data-import engine — template steps / buffer tables / queries — is a later slice).
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-mdi', label: 'Master Data Initialization', icon: 'ti-database-cog',
          children: [
            { kind: 'item', item: { id: 'set-mdi-templates', label: 'Initialization Templates', icon: 'ti-template' } },
            { kind: 'item', item: { id: 'set-mdi-log',       label: 'Initialization Log',       icon: 'ti-history' } },
          ],
      }},
      // Settings ▸ Reagent Prep — Settings-level node matching legacy (owner request 2026-09-30,
      // image 286). Calculation Editor is the ported legacy Reagent Prep ▸ Calculation Editor form
      // (dbo.RegentPrepCalculationEditor). The 6 Reagent Prep lookup masters stay under
      // Settings ▸ Utility ▸ Reagent Prep (not duplicated here).
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-reagentprep', label: 'Reagent Prep', icon: 'ti-flask-2',
          children: [
            { kind: 'item', item: { id: 'set-reagent-calceditor', label: 'Calculation Editor', icon: 'ti-calculator' } },
          ],
      }},
      // Settings ▸ Parameter — new node BEFORE Permit Library (owner request 2026-10-01, image 288/289).
      // Two forms ported from legacy: Parameter Default (Testparameter_ListView) + Parameter Library
      // (Parameter_ListView). The Data Center "Parameter Library" / "Test Parameter Defaults" views are
      // left untouched (owner: do not move them from Data Center).
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-parameter', label: 'Parameter', icon: 'ti-atom',
          children: [
            { kind: 'item', item: { id: 'set-parameter-default', label: 'Parameter Default', icon: 'ti-settings' } },
            { kind: 'item', item: { id: 'set-parameter-library', label: 'Parameter Library', icon: 'ti-book' } },
          ],
      }},
      // Settings ▸ Permit Library — legacy PermitLibrarys node (owner request 2026-09-30). Its three
      // forms already existed (scattered) and were MOVED here to avoid duplicates: Permit Library
      // (from General), Permit Type (from Utility ▸ Permit), Permit Level Limits (from Data Center).
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-permitlibrary', label: 'Permit Library', icon: 'ti-shield-half',
          children: [
            { kind: 'item', item: { id: 'pln-permit-library', label: 'Permit Library',      icon: 'ti-shield-half' } },
            { kind: 'item', item: { id: 'pln-permit-type',    label: 'Permit Type',         icon: 'ti-tags' } },
            { kind: 'item', item: { id: 'pln-permit-limits',  label: 'Permit Level Limits', icon: 'ti-settings' } },
            // Permit Row Colors (2026-10-21): the colour rules the result grids use for permit limits.
            { kind: 'item', item: { id: 'pln-permit-row-colors', label: 'Permit Row Colors', icon: 'ti-palette' } },
          ],
      }},
      // Settings ▸ Test Pricing — legacy TestPricing node (owner request 2026-09-30). The sub-forms
      // are NOT yet built in AI (only a read-only Data Center "Test Default Pricing" view exists), so
      // these are PLACEHOLDER nodes matching the legacy structure — each opens the shared empty "not
      // configured" list until its form is ported form-by-form from the old form. (Constituent Pricing
      // + Pretreatment Pricing are Visible="False" in legacy → omitted.)
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-testpricing', label: 'Test Pricing', icon: 'ti-tag',
          children: [
            { kind: 'item', item: { id: 'set-test-default-pricing',     label: 'Default Pricing',           icon: 'ti-currency-dollar' } },
            { kind: 'item', item: { id: 'set-contractlab-test-pricing', label: 'Contract Lab Test Pricing', icon: 'ti-currency-dollar' } },
            { kind: 'item', item: { id: 'set-flat-test-pricing',        label: 'Flat Test Pricing',         icon: 'ti-currency-dollar' } },
            { kind: 'subgroup', subgroup: {
                id: 'sg-set-itemcharge', label: 'Item Charge', icon: 'ti-receipt',
                children: [
                  { kind: 'item', item: { id: 'set-itemcharge-canceled', label: 'Canceled Item Charge', icon: 'ti-receipt' } },
                  { kind: 'item', item: { id: 'set-itemcharge-pricing',  label: 'Item Charge',          icon: 'ti-receipt' } },
                ],
            }},
            { kind: 'item', item: { id: 'set-test-priority', label: 'Priority', icon: 'ti-flag' } },
          ],
      }},
      // The "Dashboard" subgroup moved to System ▸ Dashboard (2026-09-26, ADR-0016).
      // General — the Settings forms that belong to no other group. Technical
      // Writer is the Help Center FAQ-article editor (legacy
      // HelpCenter_ListView_FAQ_Articles); move it to its own "Help Center"
      // folder if more help/FAQ forms are added.
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-general', label: 'General', icon: 'ti-adjustments',
          children: [
            // Bottle Sharing moved to the Analysis subgroup (owner request 2026-09-29).
            // Technical Writer moved to the Test & Method subgroup (owner request 2026-09-30).
            // Priority was REMOVED from the General group (owner request 2026-10-13) — it had
            // been moved here from the Client Request subgroup on 2026-09-30. The set-request-priority
            // form, its moduleCrud config and route remain; only this nav entry is gone. (A flat
            // search/navMeta catalog entry is kept so its form-title metadata still resolves.)
            { kind: 'item', item: { id: 'formsetup',            label: 'Form Config',      icon: 'ti-forms' } },
            { kind: 'item', item: { id: 'set-holidays',         label: 'Holidays',         icon: 'ti-calendar-event' } },
            // Permit Library moved to its own Settings ▸ Permit Library node (owner request 2026-09-30).
            // Qualifier Automation (legacy QualifierAutomation_ListView) — read-only view, owner request 2026-10-01 (image 287).
            { kind: 'item', item: { id: 'set-qualifier-automation', label: 'Qualifier Automation', icon: 'ti-wand' } },
            // Report Builder moved from General to Settings ▸ Reporting (owner request 2026-10-01, image 297).
          ],
      }},
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-test-method', label: 'Test & Method', icon: 'ti-test-pipe',
          children: [
            { kind: 'item', item: { id: 'set-methods',          label: 'Method',            icon: 'ti-check', iconColor: 'var(--ai-green)' } },
            // Round Off Test — ported legacy XAF rounding-rule test harness (owner request 2026-09-30, image 282).
            { kind: 'item', item: { id: 'set-roundofftest',     label: 'Round Off Test',    icon: 'ti-calculator' } },
            // Technical Writer moved here from the General subgroup (owner request 2026-09-30, image 280).
            { kind: 'item', item: { id: 'set-technical-writer', label: 'Technical Writer',  icon: 'ti-writing' } },
            { kind: 'item', item: { id: 'set-testcode',         label: 'Test Code',         icon: 'ti-test-pipe' } },
            { kind: 'item', item: { id: 'set-turn-around-time', label: 'Turn Around Time',  icon: 'ti-clock-hour-4' } },
          ],
      }},
      // Settings > Analysis - the legacy Settings-level Analysis group. Bottle Sharing
      // and Contract Lab moved here (owner request 2026-09-29) — Bottle Sharing from the
      // General folder, Contract Lab from the flat catalog (it had no sidebar home) —
      // joining Analysis Department Chain, Daily QC Log Book Builder and PLM Reference
      // Library. Alphabetical order. (Both keep their flat-catalog entries, so the
      // breadcrumb group stays "Settings" and nav search still finds them.)
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-analysis', label: 'Analysis', icon: 'ti-microscope',
          children: [
            { kind: 'item', item: { id: 'adc-chain',           label: 'Analysis Department Chain', icon: 'ti-sitemap' } },
            { kind: 'item', item: { id: 'set-bottlesharing',   label: 'Bottle Sharing',            icon: 'ti-bottle' } },
            { kind: 'item', item: { id: 'set-contractlab',     label: 'Contract Lab',              icon: 'ti-building-factory-2' } },
            { kind: 'item', item: { id: 'set-dailyqc-logbook', label: 'Daily QC Log Book Builder', icon: 'ti-clipboard-data' } },
            { kind: 'item', item: { id: 'set-plm-reference-library', label: 'Reference Library', icon: 'ti-microscope' } },
          ],
      }},
      // SDMS — formula configuration (2026-07-28). "Formula Library" = the SHARED
      // definitions (dbo.SpreadSheetBuilder_Formula, used by SDMS templates AND
      // Result Entry); "Result Entry Formula" = the per-Test/Matrix/Method
      // assignment that drives the Result Entry Express calculation; "SDMS Formula
      // Setup" = the per-Template assignment that drives SDMS (2026-09-01).
      // Children listed alphabetically.
      { kind: 'subgroup', subgroup: {
          id: 'sg-sdms', label: 'Formula Setup', icon: 'ti-math-function',
          children: [
            { kind: 'item', item: { id: 'formulalib', label: 'Formula Library',      icon: 'ti-math-symbols' } },
            { kind: 'item', item: { id: 'reformula',  label: 'Result Entry Express Formula Setup', icon: 'ti-math-function' } },
            { kind: 'item', item: { id: 'sdmsformula', label: 'SDMS Formula Setup', icon: 'ti-math-function' } },
          ],
      }},
      // Settings ▸ SDMS ▸ Sequence Setup — the per Test+Matrix(+Template) QC run
      // pattern (Initial / Sample / Closing) that drives the QC/Prep Sort builder.
      { kind: 'subgroup', subgroup: {
          id: 'sg-sdms-setup', label: 'SDMS', icon: 'ti-table-options',
          children: [
            // The legacy SDMS nav forms (owner request 2026-10-01, image 295/296). All read-only lists;
            // the interactive spreadsheet-template designer / formula setup are later slices. (Data Package
            // Review Checklist stays under Settings ▸ Data Package, moved 2026-09-29.)
            { kind: 'item', item: { id: 'set-sdms-scientificdata',   label: 'Scientific Data Table', icon: 'ti-table' } },
            { kind: 'item', item: { id: 'set-sdms-seqsetup',         label: 'Sequence Setup',        icon: 'ti-list-numbers' } },
            { kind: 'item', item: { id: 'set-sdms-template-builder', label: 'SDMS Template Builder',  icon: 'ti-layout-grid' } },
            { kind: 'item', item: { id: 'set-sdms-template-types',   label: 'Template Types',         icon: 'ti-category' } },
            { kind: 'item', item: { id: 'set-sdms-template-formula', label: 'Template Formula',       icon: 'ti-math-function' } },
            { kind: 'item', item: { id: 'set-sdms-formulas',         label: 'SDMS Formulas',          icon: 'ti-math-function' } },
          ],
      }},
      // Default Settings — a FOLDER, mirroring ALPACA's
      // Settings > Default Settings > ... group (user screenshot 2026-07-25).
      // Legacy order (image 192): Default Settings · Result Entry Formulas · Date
      // Filter · Report ID Format · Screen Auto Lock. Result Entry Formulas is the
      // SAME page as Formula Setup ▸ "Result Entry Express Formula Setup" (id
      // reformula) — a second LINK here to match legacy placement (owner 2026-09-29),
      // not a duplicate implementation. Date Filter (datefilter) + Report ID Format
      // (reportidformat) were added the same day as basic ports.
      { kind: 'subgroup', subgroup: {
          id: 'sg-default-settings', label: 'Default Settings', icon: 'ti-settings-cog',
          children: [
            { kind: 'item', item: { id: 'defaultsettings', label: 'Default Settings',       icon: 'ti-settings-cog' } },
            { kind: 'item', item: { id: 'reformula',       label: 'Result Entry Express Formula Setup', icon: 'ti-math-function' } },
            { kind: 'item', item: { id: 'datefilter',      label: 'Date Filter',             icon: 'ti-calendar-stats' } },
            { kind: 'item', item: { id: 'reportidformat',  label: 'Report ID Format',        icon: 'ti-id-badge-2' } },
            { kind: 'item', item: { id: 'clientversion',   label: 'Client Version Control', icon: 'ti-license' } },
            { kind: 'item', item: { id: 'alpacaappconfig', label: 'App Config',              icon: 'ti-adjustments-cog' } },
            { kind: 'item', item: { id: 'screenautolock', label: 'Screen Auto Lock',        icon: 'ti-lock-clock' } },
            { kind: 'item', item: { id: 'hotkeysconfig',  label: 'Keyboard Shortcuts',      icon: 'ti-keyboard' } },
          ],
      }},
      // Settings ▸ Pre-Login Settings (legacy XAF COCSettings, Settings group
      // Index 27). The sample-registration TEMPLATE screen — Sample Registration
      // imports one to start a job. Legacy also lists a second "Retired
      // Pre-Login" entry; that is a scope chip on this one form (rule 9). Its own
      // group node before Reporting (owner request 2026-09-30) holding the form.
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-prelogin', label: 'Pre-Login Settings', icon: 'ti-clipboard-text',
          children: [
            { kind: 'item', item: { id: 'set-prelogin', label: 'Pre-Login Settings', icon: 'ti-clipboard-text' } },
          ],
      }},
      // (Field Tests moved into the Field Template group above; Field Template / Flutter / EDD /
      // Metrc all render via their sg-set-fieldtemplate / sg-set-flutter / sg-set-edd / sg-set-metrc
      // subgroups, and their search entries live in the items: catalog above — NOT as loose children
      // here, which rendered each one twice. Fixed 2026-09-30 owner report.)
      // DEDUPE (2026-09-24): the loose top-level copies of Form Config, Holidays,
      // Method, Test Code, Technical Writer, Turn Around Time, Assign Dashboard To
      // User and Permit Library were REMOVED here. The 0bfe1ea regroup had moved
      // them into the General / Test & Method / Dashboard sub-folders above, but
      // the merge 7137a63 (TaskID 29808) re-added the old loose copies WITHOUT
      // dropping them, so each form rendered TWICE (once loose, once in its
      // folder). The folder copies above are now the single source of truth; the
      // flat `items:` search catalog still lists these ids so global nav search
      // keeps finding them.
      // External Data Import — feature configuration.  Mail Setup + Service Period
      // are popups on the External Data Import toolbar (ALPACA parity), so only the
      // grid-based config screens are menu items here.
      { kind: 'subgroup', subgroup: {
          id: 'sg-external-data-import', label: 'External Data Import', icon: 'ti-file-import',
          children: [
            { kind: 'item', item: { id: 'edi-sheet-mapping',  label: 'Sheet Mapping',    icon: 'ti-table-options' } },
            { kind: 'item', item: { id: 'edi-limits',         label: 'MCL & SCL Limits', icon: 'ti-ruler-2' } },
          ],
      }},
      // Instrument Management — legacy XAF Settings group "LabwareSettings" (2026-09-18):
      // Labware ("Instrument") · Instrument Certificate · Maintenance Setup. A
      // Settings-level group (NOT under Utility — the Utility "Instrument" group is
      // the Labware master-data lookups, a different node).
      { kind: 'subgroup', subgroup: {
          id: 'sg-instrument-management', label: 'Instrument Management', icon: 'ti-microscope',
          children: [
            { kind: 'item', item: { id: 'set-labware',             label: 'Instrument',             icon: 'ti-device-desktop-analytics' } },
            { kind: 'item', item: { id: 'set-labware-certificate', label: 'Instrument Certificate', icon: 'ti-certificate' } },
            { kind: 'item', item: { id: 'set-maintenance-setup',   label: 'Maintenance Setup',      icon: 'ti-tool' } },
          ],
      }},
      // Inventory Management — Settings-level group matching ALPACA LIMS nav position
      // (directly under Settings, same level as Instrument Management). Contains the
      // full ICM forms: Items, Vendors, Assign Items To Test, Assign Tests To Item.
      { kind: 'subgroup', subgroup: {
          id: 'sg-inventory-management', label: 'Inventory Management', icon: 'ti-packages',
          children: [
            { kind: 'item', item: { id: 'set-assign-items-to-test', label: 'Assign Items To Test',  icon: 'ti-link' } },
            { kind: 'item', item: { id: 'set-assign-tests-to-item', label: 'Assign Tests To Item',  icon: 'ti-link' } },
            { kind: 'item', item: { id: 'set-inventory-items',      label: 'Items',                 icon: 'ti-package' } },
            { kind: 'item', item: { id: 'set-vendors',              label: 'Vendors',               icon: 'ti-truck' } },
          ],
      }},
      // Settings ▸ MOR Template Setup — its own top-level GROUP node (owner request 2026-10-01, image
      // 294), moved out of the Reporting group; the MOR Template Setup form is assigned inside it.
      // Renders alphabetically between Metrc and Notification Center.
      { kind: 'subgroup', subgroup: {
          id: 'sg-set-mortemplate', label: 'MOR Template Setup', icon: 'ti-template',
          children: [
            { kind: 'item', item: { id: 'mor-template', label: 'MOR Template Setup', icon: 'ti-template' } },
          ],
      }},
      // Notification Center — lab alert/notification module, moved under Settings
      // (2026-06-26); alphabetised between Method and Reporting. Reuses the same
      // children (config forms + Browse by Category) it had at the top level.
      { kind: 'subgroup', subgroup: {
          id: NOTIFICATION_CENTER_GROUP.id, label: NOTIFICATION_CENTER_GROUP.label,
          icon: NOTIFICATION_CENTER_GROUP.icon, children: NOTIFICATION_CENTER_GROUP.children ?? [],
      }},
      // Reporting ▸ MOR Template Setup + Regulatory Compliance (alphabetised).
      { kind: 'subgroup', subgroup: {
          id: 'sg-reporting', label: 'Reporting', icon: 'ti-report',
          children: [
            // Custom Report Builder moved to the top-level Toolbox module (2026-09-28).
            // MOR Template Setup moved OUT to its own top-level Settings node (owner request 2026-10-01, image 291).
            // Report Builder moved here from General (owner request 2026-10-01, image 297).
            { kind: 'item', item: { id: 'set-report-builder', label: 'Report Builder', icon: 'ti-report' } },
            // Settings ▸ Reporting ▸ MSR — Monthly Summary Repository config
            // forms (merged team package 2026-07-02).
            { kind: 'subgroup', subgroup: {
                id: 'sg-msr', label: 'MSR', icon: 'ti-archive',
                children: [
                  { kind: 'item', item: { id: 'msr-data-import-config', label: 'MSR Data Import Config', icon: 'ti-file-import' } },
                  { kind: 'item', item: { id: 'msr-water-system',       label: 'Water System',           icon: 'ti-droplet' } },
                ],
            }},
            { kind: 'subgroup', subgroup: {
                id: 'sg-regcompliance', label: 'Regulatory Compliance', icon: 'ti-shield-check',
                children: [
                  { kind: 'item', item: { id: 'dwrc-cats',     label: 'Category',                icon: 'ti-category' } },
                  { kind: 'item', item: { id: 'regcompliance', label: 'Category and Test Setup', icon: 'ti-shield-check' } },
                  { kind: 'item', item: { id: 'dwrc-subcats',  label: 'Sub Category',            icon: 'ti-category-2' } },
                ],
            }},
          ],
      }},
      // System Management masters (ALPACA parity, merged 2026-06-26) — Division/Company,
      // Department, Position, State, Country, City, Current Language, Time Zones.
      { kind: 'subgroup', subgroup: {
          id: 'sg-system', label: 'System Management', icon: 'ti-server-cog',
          children: [
            { kind: 'item', item: { id: 'sm-city',       label: 'City',             icon: 'ti-building' } },
            { kind: 'item', item: { id: 'sm-division',   label: 'Division',         icon: 'ti-building-community' } },
            { kind: 'item', item: { id: 'sm-division-department', label: 'Division Department', icon: 'ti-building-community' } },
            { kind: 'item', item: { id: 'sm-country',    label: 'Country',          icon: 'ti-flag' } },
            { kind: 'item', item: { id: 'sm-language',   label: 'Current Language', icon: 'ti-language' } },
            { kind: 'item', item: { id: 'sm-department', label: 'Department',       icon: 'ti-sitemap' } },
            { kind: 'item', item: { id: 'sm-glossary',   label: 'Glossary',         icon: 'ti-book' } },
            { kind: 'item', item: { id: 'sm-message-editor', label: 'Message Editor', icon: 'ti-message-2' } },
            { kind: 'item', item: { id: 'sm-position',   label: 'Position',         icon: 'ti-id-badge-2' } },
            { kind: 'item', item: { id: 'sm-state',      label: 'State',            icon: 'ti-map-2' } },
            { kind: 'item', item: { id: 'sm-timezones',  label: 'Time Zones',       icon: 'ti-clock-hour-4' } },
          ],
      }},
      // (Service-only User Access Log + Exception Log moved into the
      // Settings ▸ Audit & Logs folder above — 2026-09-24 regroup. Both keep
      // their service-only gating, unchanged.)
      // App Config (id 'appconfig') stays HIDDEN during the timezone migration
      // (ADR-0008) — restore in alphabetical position when UTC is reinstated.
    ],
  } },
  { kind: 'sep' },
  // ─── SYSTEM group — internal tools (server health, alerts) ───────────────
  // Was a flat `section` header until 2026-06-10; converted to a `group` so it
  // gets the same server icon + collapse chevron as every other nav group.
  { kind: 'group', group: SYSTEM_GROUP },
  // ─── TOOLBOX group (user request 2026-09-28): Custom Report Builder · Dashboard
  //     folder · SQL Query Console — a shortcut module rendered right AFTER System. ───
  { kind: 'group', group: TOOLBOX_GROUP },
  { kind: 'sep' },
  // ─── "Audit Trail Log AI" top-level group removed from the sidebar (user
  // request 2026-09-19).  The per-AI-form audit logs are still reachable via
  // Data Center → Audit Trail Log; setAiAuditFormNav() is now a no-op (the group
  // it looked for is gone) but kept so AppShell's call site stays valid. ───
  // Data Center — mirrors ALPACA's Data Center group.  "Audit Trail Log" is a
  // collapsible sub-folder INSIDE this group (matching GoLive's nav tree), not a
  // separate top-level group.  The flat `items` list carries every leaf id
  // (DC forms + at-* audit views) so the global nav search still finds them;
  // `children` drives the rendered, nested tree.
  { kind: 'group', group: { id: 'grp-datacenter', label: 'Data Center', icon: 'ti-database',
    items: [  // flat list (search index only) — DC forms first, then audit-trail views
      // Strictly alphabetical by display label (case-insensitive).
      { id: 'dc-collector',              label: 'Client Collectors',            icon: 'ti-settings' },
      { id: 'dc-compliant',              label: 'Client Complaint',             icon: 'ti-settings' },
      { id: 'dc-contact',                label: 'Client Contacts',              icon: 'ti-settings' },
      { id: 'dc-project',                label: 'Client Projects',              icon: 'ti-settings' },
      { id: 'dc2-clients',               label: 'Clients',                      icon: 'ti-settings' },
      { id: 'dc-reporting',              label: 'Custom Reports',               icon: 'ti-settings' },
      { id: 'dc2-documents',             label: 'Documents',                    icon: 'ti-settings' },
      // 2026-06-27: Historic Results hidden from nav — its data (BT + SM archive)
      // is now merged into Result Samples (dc2-resultsamples). Route/page kept.
      { id: 'histres',                   label: 'Historic Results',             icon: 'ti-settings' },
      { id: 'dc-invoicing',              label: 'Invoices',                     icon: 'ti-settings' },
      { id: 'dc2-items',                 label: 'Items',                        icon: 'ti-settings' },
      { id: 'dc2-jobids',                label: 'Job IDs',                      icon: 'ti-settings' },
      { id: 'dc-samplenotes',            label: 'Job Notes',                    icon: 'ti-settings' },
      { id: 'dc-samplelogin',            label: 'Job Samples',                  icon: 'ti-settings' },
      { id: 'dc-sampleparam',            label: 'Job Sample Tests',             icon: 'ti-settings' },
      { id: 'dc2-labware',               label: 'Labware',                      icon: 'ti-settings' },
      { id: 'dc2-methods',               label: 'Methods',                      icon: 'ti-settings' },
      { id: 'dc-noncon',                 label: 'Non-Conformity Initiation',    icon: 'ti-settings' },
      { id: 'dc2-paramlibrary',          label: 'Parameter Library',            icon: 'ti-settings' },
      { id: 'dc-plm-contamination',      label: 'PLM Contamination Check',      icon: 'ti-settings' },
      { id: 'pln-permit-limits',         label: 'Permit Limit',                 icon: 'ti-settings' },
      { id: 'dc2-preloginparams',        label: 'Pre-Login Sample Parameters',  icon: 'ti-settings' },
      { id: 'dc2-preloginsettings',      label: 'Pre-Login Settings',           icon: 'ti-settings' },
      { id: 'dc2-analyticalbatches',     label: 'QC Batches',                   icon: 'ti-settings' },
      { id: 'dc-qctype',                 label: 'QC Types',                     icon: 'ti-settings' },
      { id: 'dc-crmquotes',              label: 'Quotes',                       icon: 'ti-settings' },
      { id: 'dc-resultqc',               label: 'Result QC',                    icon: 'ti-settings' },
      { id: 'dc2-rejectedloginsamples',  label: 'Rejected Login Samples',       icon: 'ti-settings' },
      { id: 'dc2-resultsamples',         label: 'Result Samples',               icon: 'ti-settings' },
      { id: 'dc-sdmsbatch',              label: 'SDMS Batch Results',           icon: 'ti-settings' },
      { id: 'dc2-sdmsseq',               label: 'SDMS Sequence Setup',          icon: 'ti-settings' },
      { id: 'dc2-sdmstemplates',         label: 'SDMS Templates',               icon: 'ti-settings' },
      { id: 'dc2-testcomponent',         label: 'Test Component',               icon: 'ti-settings' },
      { id: 'dc2-testcontainers',        label: 'Test Containers',              icon: 'ti-settings' },
      { id: 'dc2-testpricing',           label: 'Test Default Pricing',         icon: 'ti-settings' },
      { id: 'dc2-testholdingtime',       label: 'Test Holding Time',            icon: 'ti-settings' },
      { id: 'dc2-testinstruments',       label: 'Test Instruments',             icon: 'ti-settings' },
      { id: 'dc-testparam',              label: 'Test Parameter Defaults',      icon: 'ti-settings' },
      { id: 'dc-prepmethods',            label: 'Test Prep Methods',            icon: 'ti-settings' },
      { id: 'dc2-testqcparamdefaults',   label: 'Test QC Parameter Defaults',   icon: 'ti-settings' },
      { id: 'dc2-tests',                 label: 'Tests',                        icon: 'ti-settings' },
      { id: 'dc2-users',                 label: 'Users',                        icon: 'ti-settings' },
      { id: 'dc-loginlog',               label: 'User Login Tracking',          icon: 'ti-settings' },
      { id: 'dc2-userpermission',        label: 'User Permission',              icon: 'ti-settings' },
      { id: 'dc2-userroleright',         label: 'User Role Right',              icon: 'ti-settings' },
      { id: 'dc2-vendors',               label: 'Vendors',                      icon: 'ti-settings' },
      // Audit Trail Log sub-folder views (flat, for search).
      // "All Forms" — the GLOBAL audit view (every form's rows via all=true,
      // standing rule 11 "track all"); leads the folder so it's the first entry.
      { id: 'at-all',            label: 'All Forms',                       icon: 'ti-list-details' },
      // Settings / master-data forms — one audit-log view each (2026-06-27).
      { id: 'at-adcchain',       label: 'Analysis Department Chain',       icon: 'ti-sitemap' },
      { id: 'at-city',           label: 'City',                            icon: 'ti-building' },
      { id: 'at-country',        label: 'Country',                         icon: 'ti-flag' },
      { id: 'at-department',     label: 'Department',                      icon: 'ti-sitemap' },
      { id: 'at-division',       label: 'Division',                        icon: 'ti-building-community' },
      { id: 'at-holidays',       label: 'Holidays',                        icon: 'ti-calendar-event' },
      { id: 'at-method',         label: 'Method',                          icon: 'ti-list-check' },
      { id: 'at-paymentterms',   label: 'Payment Terms',                   icon: 'ti-cash' },
      { id: 'at-paymenttermssetup', label: 'Payment Terms Setup',          icon: 'ti-receipt' },
      { id: 'at-position',       label: 'Position',                        icon: 'ti-id-badge-2' },
      { id: 'at-region',         label: 'Region',                          icon: 'ti-map-pin' },
      { id: 'at-state',          label: 'State',                           icon: 'ti-map-2' },
      { id: 'at-casenarrative',  label: 'Case Narrative',                  icon: 'ti-file-text' },
      { id: 'at-batchreport',    label: 'Batch Reporting',                 icon: 'ti-books' },
      { id: 'at-clientrequest',  label: 'Client Request',                  icon: 'ti-clipboard-text' },
      { id: 'at-custreport',     label: 'Custom Reporting',                icon: 'ti-file-analytics' },
      { id: 'at-eddgen',         label: 'EDD Report Generator',            icon: 'ti-database-export' },
      { id: 'at-fielddata',      label: 'Field Data Entry',                icon: 'ti-map-2' },
      { id: 'at-l3subout',       label: 'Level 3 Subout Data Review',      icon: 'ti-checkup-list' },
      { id: 'at-mac',            label: 'Module Access Control',           icon: 'ti-shield-lock' },
      { id: 'at-morreport',      label: 'Ad Hoc Reporting',             icon: 'ti-report-analytics' },
      { id: 'at-morpapp',        label: 'MOR Pending Approval',            icon: 'ti-clock-check' },
      { id: 'at-morpdel',        label: 'MOR Pending Delivery',            icon: 'ti-truck-delivery' },
      { id: 'at-openquotes',     label: 'Open Quotes',                     icon: 'ti-file-dollar' },
      { id: 'at-prelogin',       label: 'Pre Login Settings',              icon: 'ti-settings' },
      { id: 'at-projectnotes',   label: 'Project Notes',                   icon: 'ti-notes' },
      { id: 'at-reptrack',       label: 'Report Tracking',                 icon: 'ti-files' },
      { id: 'at-repval',         label: 'Report Validation',               icon: 'ti-file-check' },
      { id: 'at-rappr',          label: 'Result Approval',                 icon: 'ti-circle-check' },
      { id: 'at-restrack',       label: 'Result Entry',                    icon: 'ti-list-check' },
      { id: 'at-sampreg',        label: 'Sample Registration',             icon: 'ti-clipboard-list' },
      { id: 'at-samplesites',    label: 'Sample Sites',                    icon: 'ti-map-pin' },
      { id: 'at-samptransfer',   label: 'Sample Transfer',                 icon: 'ti-arrows-exchange' },
      { id: 'at-sampaloc',       label: 'Sampling Allocation',             icon: 'ti-grid-dots' },
      { id: 'at-suboutresentry', label: 'Subout Sample Result Entry',      icon: 'ti-table' },
      { id: 'at-suboutresval',   label: 'Subout Sample Result Validation', icon: 'ti-checkup-list' },
      { id: 'at-subouttest',     label: 'Subout Test Order',               icon: 'ti-file-invoice' },
      { id: 'at-tests',          label: 'Tests',                           icon: 'ti-microscope' },
    ],
    children: [
      // "Audit Trail Log" folder sorts first alphabetically (A < C), so it leads
      // the rendered tree, then the DC forms follow in alphabetical order.
      { kind: 'subgroup', subgroup: {
          id: 'sg-audittrail', label: 'Audit Trail Log', icon: 'ti-history-toggle',
          children: [
            // "All Forms" — GLOBAL audit view (every form, all=true); leads the folder.
            { kind: 'item', item: { id: 'at-all',            label: 'All Forms',                       icon: 'ti-list-details' } },
            // Settings / master-data forms — one audit-log view each (2026-06-27).
            { kind: 'item', item: { id: 'at-adcchain',       label: 'Analysis Department Chain',       icon: 'ti-sitemap' } },
            { kind: 'item', item: { id: 'at-casenarrative',  label: 'Case Narrative',                  icon: 'ti-file-text' } },
            { kind: 'item', item: { id: 'at-city',           label: 'City',                            icon: 'ti-building' } },
            { kind: 'item', item: { id: 'at-country',        label: 'Country',                         icon: 'ti-flag' } },
            { kind: 'item', item: { id: 'at-department',     label: 'Department',                      icon: 'ti-sitemap' } },
            { kind: 'item', item: { id: 'at-division',       label: 'Division',                        icon: 'ti-building-community' } },
            { kind: 'item', item: { id: 'at-holidays',       label: 'Holidays',                        icon: 'ti-calendar-event' } },
            { kind: 'item', item: { id: 'at-method',         label: 'Method',                          icon: 'ti-list-check' } },
            { kind: 'item', item: { id: 'at-paymentterms',   label: 'Payment Terms',                   icon: 'ti-cash' } },
            { kind: 'item', item: { id: 'at-paymenttermssetup', label: 'Payment Terms Setup',          icon: 'ti-receipt' } },
            { kind: 'item', item: { id: 'at-position',       label: 'Position',                        icon: 'ti-id-badge-2' } },
            { kind: 'item', item: { id: 'at-region',         label: 'Region',                          icon: 'ti-map-pin' } },
            { kind: 'item', item: { id: 'at-state',          label: 'State',                           icon: 'ti-map-2' } },
            { kind: 'item', item: { id: 'at-batchreport',    label: 'Batch Reporting',                 icon: 'ti-books' } },
            { kind: 'item', item: { id: 'at-clientrequest',  label: 'Client Request',                  icon: 'ti-clipboard-text' } },
            { kind: 'item', item: { id: 'at-custreport',     label: 'Custom Reporting',                icon: 'ti-file-analytics' } },
            { kind: 'item', item: { id: 'at-eddgen',         label: 'EDD Report Generator',            icon: 'ti-database-export' } },
            { kind: 'item', item: { id: 'at-fielddata',      label: 'Field Data Entry',                icon: 'ti-map-2' } },
            { kind: 'item', item: { id: 'at-l3subout',       label: 'Level 3 Subout Data Review',      icon: 'ti-checkup-list' } },
            { kind: 'item', item: { id: 'at-mac',            label: 'Module Access Control',           icon: 'ti-shield-lock' } },
            { kind: 'item', item: { id: 'at-morreport',      label: 'Ad Hoc Reporting',             icon: 'ti-report-analytics' } },
            { kind: 'item', item: { id: 'at-morpapp',        label: 'MOR Pending Approval',            icon: 'ti-clock-check' } },
            { kind: 'item', item: { id: 'at-morpdel',        label: 'MOR Pending Delivery',            icon: 'ti-truck-delivery' } },
            { kind: 'item', item: { id: 'at-openquotes',     label: 'Open Quotes',                     icon: 'ti-file-dollar' } },
            { kind: 'item', item: { id: 'at-prelogin',       label: 'Pre Login Settings',              icon: 'ti-settings' } },
            { kind: 'item', item: { id: 'at-projectnotes',   label: 'Project Notes',                   icon: 'ti-notes' } },
            { kind: 'item', item: { id: 'at-reptrack',       label: 'Report Tracking',                 icon: 'ti-files' } },
            { kind: 'item', item: { id: 'at-repval',         label: 'Report Validation',               icon: 'ti-file-check' } },
            { kind: 'item', item: { id: 'at-rappr',          label: 'Result Approval',                 icon: 'ti-circle-check' } },
            { kind: 'item', item: { id: 'at-restrack',       label: 'Result Entry',                    icon: 'ti-list-check' } },
            { kind: 'item', item: { id: 'at-sampreg',        label: 'Sample Registration',             icon: 'ti-clipboard-list' } },
            { kind: 'item', item: { id: 'at-samplesites',    label: 'Sample Sites',                    icon: 'ti-map-pin' } },
            { kind: 'item', item: { id: 'at-samptransfer',   label: 'Sample Transfer',                 icon: 'ti-arrows-exchange' } },
            { kind: 'item', item: { id: 'at-sampaloc',       label: 'Sampling Allocation',             icon: 'ti-grid-dots' } },
            { kind: 'item', item: { id: 'at-suboutresentry', label: 'Subout Sample Result Entry',      icon: 'ti-table' } },
            { kind: 'item', item: { id: 'at-suboutresval',   label: 'Subout Sample Result Validation', icon: 'ti-checkup-list' } },
            { kind: 'item', item: { id: 'at-subouttest',     label: 'Subout Test Order',               icon: 'ti-file-invoice' } },
            { kind: 'item', item: { id: 'at-tests',          label: 'Tests',                           icon: 'ti-microscope' } },
          ],
      }},
      { kind: 'item', item: { id: 'dc-collector',            label: 'Client Collectors',            icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-compliant',            label: 'Client Complaint',             icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-contact',              label: 'Client Contacts',              icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-project',              label: 'Client Projects',              icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-clients',             label: 'Clients',                      icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-reporting',            label: 'Custom Reports',               icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-documents',           label: 'Documents',                    icon: 'ti-settings' } },
      // 2026-06-27: Historic Results hidden from nav — merged into Result Samples (dc2-resultsamples).
      { kind: 'item', item: { id: 'histres',                 label: 'Historic Results',             icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-invoicing',            label: 'Invoices',                     icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-items',               label: 'Items',                        icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-jobids',              label: 'Job IDs',                      icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-samplenotes',          label: 'Job Notes',                    icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-samplelogin',          label: 'Job Samples',                  icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-sampleparam',          label: 'Job Sample Tests',             icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-labware',             label: 'Labware',                      icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-methods',             label: 'Methods',                      icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-noncon',               label: 'Non-Conformity Initiation',    icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-paramlibrary',        label: 'Parameter Library',            icon: 'ti-settings' } },
      // PLM Contamination Check — one check every 50 layers per station (G5 parity 2026-10-15).
      { kind: 'item', item: { id: 'dc-plm-contamination',    label: 'PLM Contamination Check',      icon: 'ti-settings' } },
      // Permit Limit (legacy "Permit Level Limits") moved to Settings ▸ Permit Library
      // (owner request 2026-09-30, restoring the legacy structure).
      { kind: 'item', item: { id: 'dc2-preloginparams',      label: 'Pre-Login Sample Parameters',  icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-preloginsettings',    label: 'Pre-Login Settings',           icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-analyticalbatches',   label: 'QC Batches',                   icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-qctype',               label: 'QC Types',                     icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-crmquotes',            label: 'Quotes',                       icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-resultqc',             label: 'Result QC',                    icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-rejectedloginsamples', label: 'Rejected Login Samples',      icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-resultsamples',       label: 'Result Samples',               icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-sdmsbatch',            label: 'SDMS Batch Results',           icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-sdmsseq',             label: 'SDMS Sequence Setup',          icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-sdmstemplates',       label: 'SDMS Templates',               icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-testcomponent',       label: 'Test Component',               icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-testcontainers',      label: 'Test Containers',              icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-testpricing',         label: 'Test Default Pricing',         icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-testholdingtime',     label: 'Test Holding Time',            icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-testinstruments',     label: 'Test Instruments',             icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-testparam',            label: 'Test Parameter Defaults',      icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-prepmethods',          label: 'Test Prep Methods',            icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-testqcparamdefaults', label: 'Test QC Parameter Defaults',   icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-tests',               label: 'Tests',                        icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-users',               label: 'Users',                        icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc-loginlog',             label: 'User Login Tracking',          icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-userpermission',      label: 'User Permission',              icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-userroleright',       label: 'User Role Right',              icon: 'ti-settings' } },
      { kind: 'item', item: { id: 'dc2-vendors',             label: 'Vendors',                      icon: 'ti-settings' } },
    ],
  } },
];

// ─── Settings: alphabetical ordering ────────────────────────────────────────
// The Settings group grew to ~19 sub-folders and dozens of loose forms added in
// whatever order each feature landed, so the tree read as (Analysis, Reporting,
// Accounting, Test, Asbestos, …) — no order at all, despite a comment claiming
// alphabetical.  With that many entries the only findable order is A→Z, so it is
// applied HERE, once, rather than by hand-placing each new form: a list kept
// sorted by convention drifts the first time someone appends instead of inserts.
//
// Folders and loose forms sort TOGETHER by their visible label, matching how the
// sidebar draws them (a folder is not "more important" than a form, just
// expandable).  Sorting is case-insensitive and numeric-aware so "Test 2" sorts
// after "Test 1", not between "Test 1" and "Test 10".
//
// Applies ONLY to Settings — no other group's order is touched.  The admin
// form-sort (sortItems / sortChildren in AppShell) still overrides this when
// configured, so a lab can still pin its own order on top.
{
  const label = (c: NavGroupChild): string =>
    c.kind === 'item' ? c.item.label : c.kind === 'subgroup' ? c.subgroup.label : '';
  const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

  // TOP LEVEL ONLY — deliberately not recursive.
  //
  // A first attempt sorted nested folders too and broke Default Settings, where
  // App Config sits directly beneath Client Version Control to mirror ALPACA's
  // own tree (appConfigNavVisibility.test.ts asserts that adjacency, and it
  // failed immediately).  Inside a folder the grouping is small enough to read
  // and the order often carries meaning; it is the ~19-entry top level that is
  // unnavigable unsorted.  So: sort what the user scans, leave curated runs
  // alone.
  const sortTopLevel = (children: NavGroupChild[]): NavGroupChild[] => {
    // `subhead` markers label the run of items beneath them, so a tree using
    // them cannot be flat-sorted without detaching a heading from its group.
    // Settings has none today; bail out rather than silently scramble one if a
    // later change introduces them.
    if (children.some(c => c.kind === 'subhead')) return children;
    return [...children].sort((a, b) => collator.compare(label(a), label(b)));
  };

  const settings = navTree.find(e => e.kind === 'group' && e.group.id === 'grp-settings');
  if (settings && settings.kind === 'group') {
    if (settings.group.children) {
      settings.group.children = sortTopLevel(settings.group.children);
    }
    // The flat `items` list backs global nav search; sorting it keeps the
    // search results in the same order as the menu.
    settings.group.items = [...settings.group.items]
      .sort((a, b) => collator.compare(a.label, b.label));
  }
}

// ─── Settings legacy icons (user request 2026-09-19) ─────────────────────────
// The WHOLE Settings group uses the two legacy ALPACA (XAF) glyphs, matching the
// GoLive nav: every GROUP/subgroup INSIDE Settings (the "+" expandable nodes)
// shows the DevExpress `Action_Navigation` icon and every LEAF (list) shows the
// `BO_Unknown` icon.  Both PNGs live in /public/nav; NavIcon renders any *.png
// value as an <img>.  Applied to the SIDEBAR tree (the `children`) only — the
// flat `items` (which back navMeta/<FormTitle>) are left untouched so form-title
// icons don't change.  The top-level "Settings" glyph itself is NOT changed (it
// sits alongside the other top-level sections).  resolveNavIcon returns the
// node's own icon here (no per-id PNG override exists), so these images win.
{
  const GROUP_ICON = 'nav/group-nav.png';   // Action_Navigation — groups/subgroups
  const LEAF_ICON  = 'nav/bo-unknown.png';  // BO_Unknown — list leaves
  const paint = (children: NavGroupChild[] | undefined): void => {
    for (const c of children ?? []) {
      if (c.kind === 'item') c.item.icon = LEAF_ICON;
      else if (c.kind === 'subgroup') { c.subgroup.icon = GROUP_ICON; paint(c.subgroup.children); }
    }
  };
  const settings = navTree.find(e => e.kind === 'group' && e.group.id === 'grp-settings');
  if (settings && settings.kind === 'group') paint(settings.group.children);
}

/** Flat lookup: page id -> display label (for breadcrumb/title). */
export const pageTitles: Record<string, string> = (() => {
  const map: Record<string, string> = { home: 'Home' };
  for (const e of navTree) {
    if (e.kind === 'item') map[e.item.id] = e.item.label;
    if (e.kind === 'group') for (const it of e.group.items) map[it.id] = it.label;
  }
  return map;
})();

/** Flat lookup: page id -> breadcrumb group (section or group label). */
export const pageGroups: Record<string, string> = (() => {
  const map: Record<string, string> = { home: 'Overview' };
  let section = 'Overview';
  for (const e of navTree) {
    if (e.kind === 'section') section = e.label;
    if (e.kind === 'item') map[e.item.id] = section;
    if (e.kind === 'group') for (const it of e.group.items) map[it.id] = e.group.label;
  }
  return map;
})();

/** Flat lookup: page id -> its top-level nav GROUP id (e.g. 'grp-backlog').
 *  Used to auto-expand the group that contains the currently-open form so the
 *  user can see which form is active.  Walks both the flat `items` list and the
 *  nested `children` tree so every leaf page resolves. */
export const pageGroupId: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const walk = (children: NavGroupChild[] | undefined, groupId: string) => {
    for (const c of children ?? []) {
      if (c.kind === 'item') map[c.item.id] = groupId;
      else if (c.kind === 'subgroup') walk(c.subgroup.children, groupId);
    }
  };
  for (const e of navTree) {
    if (e.kind === 'group') {
      for (const it of e.group.items) map[it.id] = e.group.id;
      walk(e.group.children, e.group.id);
    }
  }
  return map;
})();

/** The SUBGROUP labels on a page's path, from its top-level nav group DOWN to the
 *  page — EXCLUDING the top-level group label and the leaf itself. Walks the nested
 *  `children` tree, so a deeply-nested leaf returns every folder on the way:
 *    'adc-chain'        → ['Analysis']
 *    'set-fiber-colors' → ['Utility','Analysis','Asbestos']  (example depth)
 *  Empty when the page sits directly under its group with no subgroup. Used to render
 *  the full breadcrumb path (Home › Group › …subgroups… › Form) so a form's navigator
 *  mirrors the sidebar tree. First match wins (a leaf lives under one group). */
export function navSubgroupPath(pageId: string): string[] {
  const walk = (children: NavGroupChild[] | undefined, trail: string[]): string[] | null => {
    for (const c of children ?? []) {
      if (c.kind === 'item') {
        if (c.item.id === pageId) return trail;
      } else if (c.kind === 'subgroup') {
        const found = walk(c.subgroup.children, [...trail, c.subgroup.label]);
        if (found) return found;
      }
    }
    return null;
  };
  for (const e of navTree) {
    if (e.kind === 'group') {
      const found = walk(e.group.children, []);
      if (found) return found;
    }
  }
  return [];
}

/** Nav metadata for one page id: its label, icon and (optional) colour. */
export interface NavMeta { id: string; label: string; icon: string; iconColor?: string; }

/** Flat lookup: page id -> { label, icon, iconColor } — the SINGLE source of
 *  truth for a form's title text, icon and colour (CLAUDE.md §18.6 task 3).
 *  Every form's on-page title (`<FormTitle pageId=…>`) reads from here, so the
 *  title text / icon / colour above a grid can never drift from that form's
 *  sidebar entry.  Mirrors exactly how AppShell renders a nav item (icon class
 *  + optional inline iconColor), so the glyph reproduces the nav glyph 1:1.
 *  First occurrence wins — the flat `items` lists (which carry iconColor) are
 *  walked before the nested `children`. */
export const navMetaById: Record<string, NavMeta> = (() => {
  const map: Record<string, NavMeta> = { home: { id: 'home', label: 'Home', icon: 'ti-home' } };
  const add = (it: NavItem) => {
    if (!map[it.id]) map[it.id] = { id: it.id, label: it.label, icon: it.icon, iconColor: it.iconColor };
  };
  const walk = (children: NavGroupChild[] | undefined) => {
    for (const c of children ?? []) {
      if (c.kind === 'item') add(c.item);
      else if (c.kind === 'subgroup') walk(c.subgroup.children);
    }
  };
  for (const e of navTree) {
    if (e.kind === 'item') add(e.item);
    if (e.kind === 'group') { for (const it of e.group.items) add(it); walk(e.group.children); }
  }
  return map;
})();

/**
 * Pages that keep their route but are NOT in the menu (G5 NavigationItems with
 * Visible=False). Registered here so their <FormTitle>, breadcrumb and tab title
 * still resolve. Not listed in the sidebar or the command palette.
 */
const HIDDEN_NAV_ITEMS: { item: NavItem; groupId: string; groupLabel: string }[] = [
  // PLM Result Entry — G5 "PLMV2" (Visible=False), opened from the QC Batch form.
  { item: { id: 'plm', label: 'PLM', icon: 'ti-microscope' }, groupId: 'grp-analysis-core', groupLabel: 'Analysis' },
  // CRM ▸ Accounts ▸ Quotes — node hidden from the menu (owner request 2026-10-30).
  // Removed from the sidebar (children) and the Home card + palette (flat items);
  // routes stay wired in AppShell, so registering here keeps each form's <FormTitle>,
  // breadcrumb and tab title resolving, and (via the HIDDEN_NAV_ITEMS skip in
  // navPaletteTargets) keeps them out of the Ctrl+K palette.
  { item: { id: 'crm-quotes-open',     label: 'Open Quotes',     icon: 'ti-file-dollar' }, groupId: 'grp-crm', groupLabel: 'CRM' },
  { item: { id: 'crm-quotes-review',   label: 'Quotes Review',   icon: 'ti-file-search' }, groupId: 'grp-crm', groupLabel: 'CRM' },
  { item: { id: 'crm-quotes-expired',  label: 'Expired Quotes',  icon: 'ti-file-x' },      groupId: 'grp-crm', groupLabel: 'CRM' },
  { item: { id: 'crm-quotes-canceled', label: 'Canceled Quotes', icon: 'ti-ban' },         groupId: 'grp-crm', groupLabel: 'CRM' },
];
for (const h of HIDDEN_NAV_ITEMS) {
  navMetaById[h.item.id] = { id: h.item.id, label: h.item.label, icon: h.item.icon };
  pageTitles[h.item.id] = h.item.label;
  pageGroups[h.item.id] = h.groupLabel;
  pageGroupId[h.item.id] = h.groupId;
}

/** Top-level nav GROUP id → its display label (e.g. 'grp-settings' → 'Settings'). */
const groupIdToLabel: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const e of navTree) if (e.kind === 'group') map[e.group.id] = e.group.label;
  return map;
})();

/** Every navigable leaf page id + a human group label — the source list for the
 *  Ctrl/⌘+K command palette's "jump to any form" entries (ADR-0013 Phase 3, the
 *  mouse-free "go to any of the ~150 forms" launcher).  Derived from `navMetaById`
 *  (which flattens every flat + nested leaf), so a NEW form appears automatically.
 *  The palette label is resolved at call time via `navLabel` (honouring admin
 *  renames) and AppShell filters these by permission (`canSee`) before publishing
 *  them, so a user only ever sees the forms they can open. */
// Top-level nav groups excluded from the Ctrl+K palette (user request 2026-09-21):
// the To Do List group is saved backlog / dashboard VIEWS (Task Tracker + Sample
// Backlog — Backlog 00–07, Missing COC, …), not distinct forms, so they were noise
// in a "jump to a form" list. Add a group id here to keep its leaves out of the
// palette; they remain reachable from the sidebar.
const PALETTE_EXCLUDE_GROUPS = new Set<string>(['grp-todolist']);

export const navPaletteTargets: { id: string; group: string }[] = (() => {
  const out: { id: string; group: string }[] = [];
  for (const id of Object.keys(navMetaById)) {
    const gid = pageGroupId[id];
    if (gid && PALETTE_EXCLUDE_GROUPS.has(gid)) continue;   // skip To Do List views
    if (HIDDEN_NAV_ITEMS.some(h => h.item.id === id)) continue; // hidden from the menu, as in G5
    const group = (gid ? groupIdToLabel[gid] : undefined) ?? pageGroups[id] ?? 'Go to';
    out.push({ id, group });
  }
  return out;
})();

/** Runtime nav-caption overrides — page id → caption.  Populated by AppShell from
 *  `GET /api/v1/navigation/captions` (the live `dbo.NavigationItem.NavigationCaption`
 *  column).  Empty by default, so the curated labels above are the fallback and nothing
 *  changes until an admin renames a node in the Navigation Manager (CLAUDE.md §0d). */
let _captionOverrides: Record<string, string> = {};

/** Replace the runtime caption-override map (AppShell calls this after fetching the feed). */
export function setNavCaptionOverrides(map: Record<string, string>): void {
  _captionOverrides = map ?? {};
}

/** Resolve a page id to its display label, honouring an admin's runtime rename.
 *  Order: runtime override → curated nav label → page-title map → fallback → id. */
export function navLabel(id: string, fallback?: string): string {
  const label = _captionOverrides[id] ?? navMetaById[id]?.label ?? pageTitles[id] ?? fallback ?? id;
  // Abbreviate the Sample Backlog dashboard prefix "Backlog NN …" → "BL NN …"
  // (user request 2026-09-19: "Backlog" takes too much space). The caption for
  // these dashboards is DB-driven (DashboardData), so the swap is applied here at
  // render — regardless of source — and only when "Backlog" is followed by a
  // digit, so the "Sample Backlog" group label is untouched.
  return label.replace(/\bBacklog (?=\d)/g, 'BL ');
}

/** The CURATED (code) label for a page id, ignoring any runtime override.  AppShell
 *  uses this as the baseline when deciding whether a DB caption is an admin rename
 *  (DB caption differs from the curated label → override the sidebar). */
export function rawNavLabel(id: string): string | undefined {
  return navMetaById[id]?.label ?? pageTitles[id];
}

/** Normalise a module label / name for matching a sidebar group to a DB module
 *  (lower-case, "&" → "and", collapse whitespace) — e.g. "User & Security" ↔ "User and Security". */
export const normMod = (s: string) => s.toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ').trim();

/** Aliases for modules whose DB name differs from the sidebar label, so both canonicalise
 *  to the SAME key (normalised).  Extend as mismatches surface. */
const MODULE_ALIASES: Record<string, string> = {
  // DB "Sample SubOut Tracking" ↔ sidebar "Sample SubContract Tracking"
  'sample subout tracking': 'sample subcontract tracking',
};

/** Canonical module key for matching a sidebar group to a DB module — `normMod` + alias map. */
export const canonMod = (s: string) => { const n = normMod(s); return MODULE_ALIASES[n] ?? n; };

/** All leaf (clickable item) page ids under a group — its flat `items` plus every
 *  item nested in its `children` sub-groups.  AppShell uses these to resolve a group
 *  header's DB caption from one of its child items' Parent (CLAUDE.md §0d). */
export function collectGroupLeafIds(group: NavGroup): string[] {
  const ids: string[] = group.items.map(it => it.id);
  const walk = (children: NavGroupChild[] | undefined) => {
    for (const c of children ?? []) {
      if (c.kind === 'item') ids.push(c.item.id);
      else if (c.kind === 'subgroup') walk(c.subgroup.children);
    }
  };
  walk(group.children);
  return ids;
}

/** Every page id under Settings ▸ Utility (the `sg-utility` sub-group, all depths).
 *  AppShell tags these pages — plus every Data Center page — so the shared 24px row
 *  height (shell.css, `data-compact-grid`) applies to exactly those two areas. */
export const utilityPageIds: ReadonlySet<string> = (() => {
  const ids = new Set<string>();
  const collect = (children: NavGroupChild[] | undefined) => {
    for (const c of children ?? []) {
      if (c.kind === 'item') ids.add(c.item.id);
      else if (c.kind === 'subgroup') collect(c.subgroup.children);
    }
  };
  const find = (children: NavGroupChild[] | undefined): boolean => {
    for (const c of children ?? []) {
      if (c.kind !== 'subgroup') continue;
      if (c.subgroup.id === 'sg-utility') { collect(c.subgroup.children); return true; }
      if (find(c.subgroup.children)) return true;
    }
    return false;
  };
  for (const e of navTree) if (e.kind === 'group' && find(e.group.children)) break;
  return ids;
})();

/** Resolve a page id to its nav label/icon/colour (undefined when unknown,
 *  e.g. a hidden form not in the sidebar).  The label folds in any runtime
 *  caption override so `<FormTitle>` reflects a renamed node too. */
export function navMeta(id: string): NavMeta | undefined {
  const m = navMetaById[id];
  if (m) {
    const meta = _captionOverrides[id] ? { ...m, label: _captionOverrides[id] } : m;
    // Global legacy-icon resolver — <FormTitle> matches the sidebar (2026-09-18).
    return { ...meta, icon: resolveNavIcon(id, meta.icon) };
  }
  return _captionOverrides[id] ? { id, label: _captionOverrides[id], icon: 'ti-table' } : undefined;
}

/**
 * The TOP-LEVEL group a page id sits under, e.g. 'messagelibrary' -> 'Toolbox'.
 *
 * For a breadcrumb. Hardcoding the group is how one goes stale: Message Library
 * moved from System to Toolbox and its trail kept saying System (user report
 * 2026-09-29). Derived from the same tree the sidebar renders, so the two cannot
 * disagree. `undefined` for a page that is not in any group (a top-level item).
 */
export function navGroupLabel(id: string): string | undefined {
  const inSub = (sub: NavSubGroup): boolean =>
    (sub.children ?? []).some((c) =>
      (c.kind === 'item' && c.item.id === id)
      || (c.kind === 'subgroup' && inSub(c.subgroup)));

  for (const entry of navTree) {
    if (entry.kind !== 'group') continue;
    const g = entry.group;
    if (g.items.some((it) => it.id === id)) return g.label;
    if ((g.children ?? []).some((c) =>
      (c.kind === 'item' && c.item.id === id)
      || (c.kind === 'subgroup' && inSub(c.subgroup)))) return g.label;
  }
  return undefined;
}

/** True when a page id is a leaf UNDER Settings ▸ Utility (the `sg-utility`
 *  subgroup). Drives the Utility detail-view styling — de-yellow + square
 *  controls (the shared `.sd-grid--square`) — across EVERY Utility form without
 *  tagging each moduleCrud entry (owner request 2026-10-13). Derived from the
 *  same tree the sidebar renders, so a Utility form added later is covered
 *  automatically. Computed once (memoised). */
let _utilityFormIds: Set<string> | null = null;
export function isUtilityForm(id: string): boolean {
  if (_utilityFormIds === null) {
    const ids = new Set<string>();
    const collect = (children: NavGroupChild[]): void => {
      for (const c of children) {
        if (c.kind === 'item') ids.add(c.item.id);
        else if (c.kind === 'subgroup') collect(c.subgroup.children);
      }
    };
    const findUtility = (children: NavGroupChild[]): boolean => {
      for (const c of children) {
        if (c.kind !== 'subgroup') continue;
        if (c.subgroup.id === 'sg-utility') { collect(c.subgroup.children); return true; }
        if (findUtility(c.subgroup.children)) return true;
      }
      return false;
    };
    for (const entry of navTree) {
      if (entry.kind === 'group' && entry.group.children && findUtility(entry.group.children)) break;
    }
    _utilityFormIds = ids;
  }
  return _utilityFormIds.has(id);
}

/** The "Audit Trail Log AI" group id + its consolidated (All AI Forms) leaf. */
const AUDIT_AI_GROUP_ID = 'grp-audittrail-ai';
const AUDIT_AI_ALL_ITEM: NavItem = { id: 'at-ai', label: 'Audit Trail Log AI', icon: 'ti-shield-check' };

/** Populate the "Audit Trail Log AI" nav group with ONE leaf PER AI form that has
 *  audit data — so the sidebar itself is the coverage inventory (user request
 *  2026-07-10: "create each form each navigation … then only i know which form has
 *  audit trail in AI").  Called by AppShell after it fetches the forms-with-data
 *  from /api/v1/audit/forms.  The consolidated "Audit Trail Log AI" (at-ai) leaf is
 *  always kept FIRST, then the per-form leaves in the given order.  Idempotent —
 *  each call REPLACES the group's items (so a form that loses all its rows drops
 *  off).  Mutates navTree + the derived lookups in place; AppShell bumps a render
 *  tick so the sidebar re-reads them (orderedTree returns navTree by reference). */
export function setAiAuditFormNav(forms: { id: string; label: string; count?: number }[]): void {
  const entry = navTree.find(e => e.kind === 'group' && e.group.id === AUDIT_AI_GROUP_ID);
  if (!entry || entry.kind !== 'group') return;
  const g = entry.group;
  const items: NavItem[] = [
    AUDIT_AI_ALL_ITEM,
    ...forms.map(f => ({ id: f.id, label: f.label, icon: 'ti-shield-check', count: f.count })),
  ];
  g.items = items;
  // Register the derived lookups for the dynamic ids so labels, breadcrumbs,
  // group auto-expand and <FormTitle> all resolve (they're computed once from the
  // static tree, so new ids must be added here explicitly).
  for (const it of items) {
    pageTitles[it.id]   = it.label;
    pageGroups[it.id]   = g.label;
    pageGroupId[it.id]  = g.id;
    navMetaById[it.id]  = { id: it.id, label: it.label, icon: it.icon };
  }
}

/** The shared legacy dashboard glyph every Sample Backlog entry uses. */
const BACKLOG_NAV_ICON = 'img:nav/bo-dashboard.png';

/** DYNAMIC NAV (2026-10-06): rebuild the "Sample Backlog" subgroup's children from
 *  the dashboards ACTUALLY ASSIGNED to that nav group (the catalog from
 *  /api/v1/backlog/nav-dashboards → dbo.AssignDashboardToUserDepartment). Called by
 *  AppShell after it fetches the catalog — so assigning a new dashboard to the
 *  "Sample Backlog" navigation item makes it appear automatically, exactly like
 *  legacy ALPACA (replaces the hardcoded list).
 *
 *  KNOWN ids (already in the static list) keep their CURATED label + relative order
 *  so nothing existing jumps or renames; a dashboard not in the static list is
 *  appended with its DashboardData Title. A static id NOT in the catalog
 *  (unassigned/inactive) drops off, so the menu tracks the assignment table.
 *  Per-user VISIBILITY is still enforced by canSeeNavItem (allowedDashboards) — this
 *  only decides MEMBERSHIP + order. Idempotent; mutates navTree + the derived lookups
 *  in place (AppShell bumps a render tick; orderedTree returns navTree by reference). */
/** Prefix for a per-category Documents leaf. The category Oid follows, so the id
 *  is stable across renames — unlike ALPACA, which keys the generated node on the
 *  category NAME and loses the grant when the lab renames one. */
export const DOC_CATEGORY_PREFIX = 'documents:';

/** The category Oid a `documents:<oid>` page id carries, or null for the plain
 *  `documents` page (= "no category chosen, open the first"). */
export function documentCategoryOf(pageId: string): string | null {
  return pageId.startsWith(DOC_CATEGORY_PREFIX) ? pageId.slice(DOC_CATEGORY_PREFIX.length) : null;
}

/** Replace the Documents subgroup's children with ONE LEAF PER DOCUMENT CATEGORY
 *  — G5 parity (owner request 2026-10-12). AppShell calls this after
 *  `GET /api/v1/published-documents/categories`, then bumps its render tick.
 *  Idempotent; mutates navTree + the derived lookups in place, exactly like
 *  `setBacklogNav`. An EMPTY list leaves the seeded plain "Documents" leaf
 *  alone, so the form never becomes unreachable just because nothing is
 *  published yet. */
export function setDocumentCategoryNav(cats: { id: string; name: string }[]): void {
  const grp = navTree.find(e => e.kind === 'group' && e.group.id === 'grp-docmanagement');
  if (!grp || grp.kind !== 'group') return;
  const subEntry = grp.group.children?.find(c => c.kind === 'subgroup' && c.subgroup.id === 'sg-documents');
  if (!subEntry || subEntry.kind !== 'subgroup') return;
  if (cats.length === 0) return;

  const icon = 'img:nav/bo-unknown.png';
  subEntry.subgroup.children = cats.map(c => {
    const id = DOC_CATEGORY_PREFIX + c.id;
    pageTitles[id]  = c.name;
    pageGroups[id]  = 'Document Management';
    pageGroupId[id] = 'grp-docmanagement';
    navMetaById[id] = { id, label: c.name, icon };
    return { kind: 'item' as const, item: { id, label: c.name, icon } };
  });
  // Keep `items` in step so leaf collection / the command palette see them too.
  grp.group.items = cats.map(c => ({ id: DOC_CATEGORY_PREFIX + c.id, label: c.name, icon }));
}

export function setBacklogNav(items: { id: string; label: string }[]): void {
  const todo = navTree.find(e => e.kind === 'group' && e.group.id === 'grp-todolist');
  if (!todo || todo.kind !== 'group') return;
  const subEntry = todo.group.children?.find(
    c => c.kind === 'subgroup' && c.subgroup.id === 'grp-backlog');
  if (!subEntry || subEntry.kind !== 'subgroup') return;
  const sub = subEntry.subgroup;

  // Existing curated label + order for the static ids (so known entries keep their
  // label and position; new dashboards are appended after them, in catalog order).
  const existing = new Map<string, { idx: number; label: string }>();
  sub.children.forEach((c, i) => { if (c.kind === 'item') existing.set(c.item.id, { idx: i, label: c.item.label }); });

  const known = items.filter(it => existing.has(it.id))
                     .sort((a, b) => existing.get(a.id)!.idx - existing.get(b.id)!.idx);
  const fresh = items.filter(it => !existing.has(it.id));

  sub.children = [...known, ...fresh].map(it => {
    const label = existing.get(it.id)?.label ?? it.label;   // keep curated label for known ids
    const item: NavItem = { id: it.id, label, icon: BACKLOG_NAV_ICON };
    pageTitles[it.id]  = label;
    pageGroups[it.id]  = sub.label;
    pageGroupId[it.id] = sub.id;
    navMetaById[it.id] = { id: it.id, label, icon: BACKLOG_NAV_ICON };
    return { kind: 'item', item };
  });
}
